/**
 * Sidenavigation Component
 * 
 * This component provides the main navigation sidebar for the application with
 * role-based navigation, user profile management, and responsive design.
 * It handles navigation state, user authentication, and dynamic menu generation.
 * 
 * Key Features:
 * - Role-based navigation items
 * - User profile dropdown
 * - Responsive sidebar with collapse/expand
 * - Active route highlighting
 * - Permission-based menu items
 * - Theme switching
 * - User session management
 * 
 * @author Your Name
 * @version 1.0.0
 */

import { RouterOutlet } from '@angular/router';
import { Router, ActivatedRoute } from '@angular/router';
import { TuiTagModule } from '@taiga-ui/legacy';
import {
  ChangeDetectionStrategy,
  Component,
  signal,
  effect,
  computed,
  inject,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
  ElementRef,
  Renderer2,
  HostListener,
  ChangeDetectorRef,
  AfterViewInit,
} from '@angular/core';
import {
  AsyncPipe,
  NgTemplateOutlet,
  NgClass,
  NgFor,
  CommonModule,
} from '@angular/common';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import {
  RouterLink,
  RouterLinkActive
} from '@angular/router';

import {
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiDropdownService,
  TuiFallbackSrcPipe,
  TuiIcon,
  TuiLink,
  TuiOption,
  TuiTextfield,
  TuiTitle,
} from '@taiga-ui/core';

import {
  TuiAvatar,
  TuiBadgeNotification,
  TuiBadge,
  TuiChevron,
  TuiFade,
  TuiSegmented,
  TuiAvatarOutline,
  TuiChip,
  TuiDataListWrapper,
  TuiSelect,
  TuiTooltip,
  tuiItemsHandlersProvider,
  TUI_CONFIRM,
  TuiSkeleton
} from '@taiga-ui/kit';

import type { TuiConfirmData } from '@taiga-ui/kit';

import {
  TuiNavigation,
  TuiInputSearch,
  TuiCardMedium,
  TuiCell,
  TuiHeader
} from '@taiga-ui/layout';
import { TUI_DEFAULT_MATCHER, TuiClickOutside } from '@taiga-ui/cdk';
import { TuiSearchResults } from '@taiga-ui/experimental';
import { filter, map, startWith, switchMap, timer, Subscription } from 'rxjs';
import { UiThemeService } from '../../service/ui-theme';
import { NavigationEnd } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { NavPrefetchService } from '../../services/nav-prefetch.service';
import { OrganizationStateService } from '../../services/organization-state.service';
import { getCookie } from '../../utils/cookie.utils';
import { NavigationService, NavigationItem } from '../../services/navigation.service';
import { SetupGuideService } from '../../services/setup-guide.service';
import { RoleService } from '../../services/role.service';
import { NotificationService } from '../../services/notification.service';
import { TimezoneService } from '../../services/timezone.service';
import { GlobalTimezoneService } from '../../services/global-timezone.service';
import { SimpleRoleSwitcherComponent } from '../../components/simple-role-switcher/simple-role-switcher';
import { PlanDetailsComponent } from '../../../client_app/plan-details/plan-details';
import { NotificationBanner } from '../../components/notification-banner/notification-banner';
import { InputHeading } from '../../components/input-heading/input-heading';
import { FullpageDrawer } from '../../components/drawers/fullpage-drawer/fullpage-drawer';
import { SupportDrawer } from '../../components/drawers/support-drawer/support-drawer';
import { LanguageSwitcher } from '../../components/language-switcher/language-switcher';
import type { PolymorpheusContent } from '@taiga-ui/polymorpheus';
import type { TuiDialogContext, TuiDialogSize } from '@taiga-ui/core';
import { ParkingModalService } from '../../services/parking-modal.service';

interface Result {
  href: string;
  title: string;
  subtitle?: string;
  icon?: string;
}

const DATA: Record<string, readonly Result[]> = {
  Documents: [
    {
      title: 'Monty Python',
      href: 'https://en.wikipedia.org/wiki/Monty_Python',
    },
  ],
  Code: [
    {
      title: 'Taiga UI',
      href: 'https://github.com/taiga-family/taiga-ui',
      icon: '@tui.github',
    },
    {
      title: 'Maskito',
      href: 'https://github.com/taiga-family/maskito',
      icon: '@tui.github',
    },
    {
      title: 'Web APIs for Angular',
      href: 'https://github.com/taiga-family/angular-web-apis',
      icon: '@tui.github',
    },
  ],
  Design: [
    {
      title: 'Figma',
      href: 'https://figma.com',
      icon: '@tui.external-link',
    },
  ],
};

@Component({
  standalone: true,
  selector: 'app-sidenavigation',
  imports: [
    AsyncPipe,
    NgFor,
    RouterLink,
    RouterLinkActive,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TuiDropdown,
    // Taiga UI
    TuiAppearance,
    TuiAvatar,
    TuiBadgeNotification,
    TuiButton,
    TuiChevron,
    TuiDataList,
    TuiOption,
    TuiCardMedium,
    TuiCell,
    TuiDropdown,
    TuiFade,
    TuiIcon,
    TuiNavigation,
    TuiTextfield,
    TuiTitle,
    TuiSearchResults,
    TuiSegmented,
    NgClass,
    TuiFallbackSrcPipe,
    TuiTagModule,
    TuiChip,
    TuiSkeleton,
    InputHeading,
    // Custom components
    PlanDetailsComponent,
    NotificationBanner,
    TuiDataListWrapper,
    TuiSelect,
    SimpleRoleSwitcherComponent,
    SupportDrawer,
    LanguageSwitcher,
    TuiHeader
  ],
  templateUrl: './sidenavigation.html',
  styleUrl: './sidenavigation.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    tuiItemsHandlersProvider({
      identityMatcher: (item1: any, item2: any) => {
        const id1 = item1?.id || item1;
        const id2 = item2?.id || item2;
        return id1 && id2 ? id1 === id2 : item1 === item2;
      },
    }),
  ],
})
export class Sidenavigation implements OnInit, OnDestroy, AfterViewInit {
  @Input() hideNavigation: boolean = false;
  @ViewChild('planDetailsComponent') planDetailsComponent!: PlanDetailsComponent

  // Flag to prevent infinite refresh loops
  private isRedirecting = false;
  /** Throttle: minimum ms between setup completion checks on navigation */
  private readonly SETUP_CHECK_THROTTLE_MS = 15_000;
  private lastSetupCheckTime = 0;

  /** Call checkSetupCompletion at most once per SETUP_CHECK_THROTTLE_MS */
  private throttledSetupCheck(delay: number = 0, forceRefresh: boolean = false): void {
    // 1. Skip check entirely for Super Admins
    const currentUser = this.apiService.getCurrentUser();
    const userRole = currentUser?.role_id || currentUser?.role?.name || currentUser?.type;
    const normalizedRole = userRole?.toLowerCase();
    const isSuperAdmin = normalizedRole === 'super admin' || currentUser?.email === 'system_admin@kormoan.com';

    if (isSuperAdmin) {
      localStorage.setItem('is_setup_complete', 'true');
      return;
    }

    const performCheck = () => {
      const now = Date.now();
      if (forceRefresh || now - this.lastSetupCheckTime > this.SETUP_CHECK_THROTTLE_MS) {
        this.lastSetupCheckTime = now;
        this.setupGuideService.checkSetupCompletion(forceRefresh).subscribe({
          next: () => {
            // Reload nav after check so Setup Guide tab hides without a full refresh
            this.loadNavigationItems();
            this.cdr.detectChanges();
          }
        });
      }
    };

    if (delay > 0) {
      setTimeout(performCheck, delay);
    } else {
      performCheck();
    }
  }

  // Retry counter for loading navigation items
  private loadNavigationRetryCount = 0;
  private readonly MAX_NAVIGATION_LOAD_RETRIES = 10;

  protected expanded = signal(false);
  protected open = false;
  protected switch = false;
  protected hasNotificationBanner = signal<boolean>(false);
  protected notificationsOpen = signal(false);
  protected readonly supportDrawerOpen = signal(false);
  protected readonly supportDrawerExpanded = signal(false);

  /** Phone layout: the existing sidebar opens as an overlay from the menu button. */
  protected readonly isPhoneLayout = signal(typeof window !== 'undefined' && window.innerWidth <= 768);
  protected readonly mobileNavOpen = signal(false);
  /** Phone drawer step: false = main menu, true = settings menu. */
  protected readonly mobileSettingsStep = signal(false);

  protected mockNotifications = signal<any[]>([]);

  protected unreadCount = computed(() =>
    this.mockNotifications().filter(n => !n.isRead).length
  );

  protected activeNotificationTab = signal(0);

  protected filteredNotifications = computed(() => {
    const notifications = this.mockNotifications();
    return this.activeNotificationTab() === 0
      ? notifications
      : notifications.filter(n => !n.isRead);
  });

  // Current URL signal for active route detection with OnPush change detection
  // Initialize with empty string, will be set in ngOnInit after router is available
  protected currentUrl = signal<string>('');

  // Computed height for aside element
  protected asideHeight = computed(() => {
    return this.hasNotificationBanner() ? 'calc(100vh - 88px)' : 'calc(100vh - 48px)';
  });
  protected readonly routes: any = {};
  isOpen = false;
  currentUser: any = null;
  protected navigationItems: NavigationItem[] = [];
  protected settingsNavigationItems: NavigationItem[] = [];
  protected userRole: string = 'user';
  private roleSubscription: Subscription = new Subscription();
  private lastLoadedPlanParkingId: string | null = null;

  // Simple backup property for organization name
  protected organizationName: string = 'Sistem Apps';

  // Vendor view mode properties
  protected isVendorViewMode: boolean = false;
  protected selectedParkingId: string | null = null;
  protected selectedParkingName: string | null = null;
  protected parkingList: any[] = [];
  protected loadingParking = signal(false);
  protected parkingControl = new FormControl<any>(null);
  private parkingSubscription: Subscription = new Subscription();
  private setupSubscription: Subscription = new Subscription();
  private statusCheckInProgress = false;
  private lastStatusCheckAt = 0;
  private readonly STATUS_CHECK_INTERVAL_MS = 60_000;
  private lastParkingDetailFetch: { id: string; at: number } | null = null;
  private readonly PARKING_DETAIL_REFRESH_MS = 60_000;
  private forcedLogoutInProgress = false;

  // Loading overlay state

  // Plan information properties
  protected planName: string | null = null;
  protected planExpiryDate: string | null = null;
  protected isPlanCancelled: boolean = false;
  protected isPlanExpired: boolean = false;
  protected isTrial: boolean = false;

  // Cache for dynamic router links to prevent infinite change detection loops
  private routerLinkCache: Map<string, string[]> = new Map();

  // Caching for storage lookups to avoid repetitive parsing during change detection
  private storageCache = new Map<string, { value: any, timestamp: number }>();
  private readonly STORAGE_CACHE_TTL = 1000; // 1 second cache for very frequent reads

  private getCachedStorageItem(key: string, isJson: boolean = false, useSession: boolean = false): any {
    const now = Date.now();
    const cached = this.storageCache.get(key);

    if (cached && (now - cached.timestamp < this.STORAGE_CACHE_TTL)) {
      return cached.value;
    }

    const raw = useSession ? sessionStorage.getItem(key) : localStorage.getItem(key);
    let value = raw;
    if (key === 'user_pov' && !value) {
      value = sessionStorage.getItem('user_pov');
    }

    if (isJson && raw) {
      try {
        value = JSON.parse(raw);
      } catch (e) {
        value = null;
      }
    }

    this.storageCache.set(key, { value, timestamp: now });
    return value;
  }

  // Timezone services for date formatting
  private readonly timezoneService = inject(TimezoneService);
  private readonly globalTimezoneService = inject(GlobalTimezoneService);

  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    const element = event.target as HTMLElement;
    const avatar = document.querySelector('.avatart-icon') as HTMLElement;
    const dropdown = document.querySelector('.profile-drop') as HTMLElement;

    if (avatar && dropdown && !avatar.contains(element) && !dropdown.contains(element)) {
      this.isOpen = false;
    }
  }

  @HostListener('window:focus')
  onWindowFocus(): void {
    this.syncUserProfileOnFocus();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      this.syncUserProfileOnFocus();
    }
  }

  @HostListener('window:resize')
  protected onPhoneLayoutResize(): void {
    const phone = typeof window !== 'undefined' && window.innerWidth <= 768;
    this.isPhoneLayout.set(phone);
    if (!phone) {
      this.mobileSettingsStep.set(false);
      this.closeMobileNav();
    }
  }

  @HostListener('window:storage', ['$event'])
  onStorageEvent(event: StorageEvent): void {
    if (event.key === 'user_info' && event.newValue) {
      try {
        const parsed = JSON.parse(event.newValue);
        if (parsed && parsed.id) {
          (this.apiService as any).currentUserSubject?.next(parsed);
        }
      } catch (e) { }
    }
  }

  // Throttle: prevent API spam from rapid focus/visibility events
  private lastFocusSyncTime = 0;
  private readonly FOCUS_SYNC_THROTTLE_MS = 30000; // 30s min between focus-triggered syncs

  private syncUserProfileOnFocus(): void {
    const isAuth = !!this.apiService.getAuthToken();
    if (!isAuth) return;
    const now = Date.now();
    if (now - this.lastFocusSyncTime < this.FOCUS_SYNC_THROTTLE_MS) return; // skip if too soon
    this.lastFocusSyncTime = now;
    this.apiService.refreshCurrentUserFromServer().subscribe();
  }

  protected readonly breadcrumbs = ['Home', 'Angular', 'Repositories', 'Taiga UI'];
  protected readonly control = new FormControl('');
  protected readonly popular = ['Taiga UI', 'Maskito', 'Web APIs for Angular'];

  protected getUserRole(): string {
    // Get the current active role from RoleService
    return this.roleService.getCurrentRole();
  }

  /**
   * Check if the role switcher should be visible
   */
  protected shouldShowRoleSwitcher(): boolean {
    if (!this.currentUser) return false;

    // Check if user has admin or super admin role
    const userRole = this.currentUser.role_id || this.currentUser.role?.name || this.currentUser.type;
    const normalizedRole = userRole?.toLowerCase();

    // Don't show role switcher for super admin users
    if (normalizedRole === 'super admin' || this.currentUser.email === 'system_admin@kormoan.com') {
      return false;
    }

    const hasAdminRole = normalizedRole === 'admin' ||
      this.currentUser.role_id?.toLowerCase().includes('admin') ||
      this.currentUser.type === 'admin'; // Direct type check

    // Show role switcher for admin users or if user is currently in a manual switch
    return hasAdminRole || this.roleService.isManualRoleSwitch();
  }

  /**
   * Get the current role status for display
   */
  protected getCurrentRoleStatus(): string {
    const currentRole = this.roleService.getCurrentRole();
    const originalRole = this.roleService.getOriginalRole();

    if (this.roleService.isManualRoleSwitch()) {
      return `Switched from ${originalRole} to ${currentRole}`;
    }

    return `Current role: ${currentRole}`;
  }

  /**
   * Switch back to the user's original role
   */
  protected switchToOriginalRole(): void {
    this.resetToOriginalRole();
  }

  /**
   * Switch to vendor role (manual switch)
   */
  protected switchToVendorRole(): void {
    this.roleService.switchRole('vendor');
    this.switchToVendorNavigation();
  }

  /**
   * Switch to admin role (manual switch)
   */
  protected switchToAdminRole(): void {
    this.roleService.switchRole('admin');
    this.switchToAdminNavigation();
  }

  /**
   * Check if the user is currently in vendor mode
   */
  protected isInVendorMode(): boolean {
    return this.roleService.getCurrentRole().toLowerCase() === 'vendor';
  }

  /**
   * Check if the user is currently in admin mode
   */
  protected isInAdminMode(): boolean {
    return this.roleService.getCurrentRole().toLowerCase() === 'admin';
  }

  /**
   * Get the role switcher icon
   */
  protected getRoleSwitcherIcon(): string {
    if (this.roleService.isManualRoleSwitch()) {
      return 'tuiIconRefresh'; // Show refresh icon for manual switches
    }

    const currentRole = this.roleService.getCurrentRole();
    if (currentRole.toLowerCase() === 'vendor') {
      return 'tuiIconUser'; // Show user icon for vendor role
    } else {
      return 'tuiIconSettings'; // Show settings icon for admin role
    }
  }

  /**
   * Get the role switcher CSS class
   */
  protected getRoleSwitcherCssClass(): string {
    if (this.roleService.isManualRoleSwitch()) {
      return 'role-switcher-manual';
    }

    const currentRole = this.roleService.getCurrentRole();
    if (currentRole.toLowerCase() === 'vendor') {
      return 'role-switcher-vendor';
    } else {
      return 'role-switcher-admin';
    }
  }

  /**
   * Get the role switcher badge text
   */
  protected getRoleSwitcherBadgeText(): string {
    if (this.roleService.isManualRoleSwitch()) {
      return 'Manual';
    }

    const currentRole = this.roleService.getCurrentRole();
    if (currentRole.toLowerCase() === 'vendor') {
      return 'Vendor';
    } else {
      return 'Admin';
    }
  }

  /**
   * Get the role switcher description
   */
  protected getRoleSwitcherDescription(): string {
    if (this.roleService.isManualRoleSwitch()) {
      const originalRole = this.roleService.getOriginalRole();
      const currentRole = this.roleService.getCurrentRole();
      return `You are currently viewing the system as ${currentRole} (switched from ${originalRole})`;
    }

    const currentRole = this.roleService.getCurrentRole();
    return `Current role: ${currentRole}`;
  }

  /**
   * Handle role switcher click with confirmation
   */
  protected handleRoleSwitcherClick(): void {
    if (this.roleService.isManualRoleSwitch()) {
      // If user is in a manual switch, confirm before resetting
      if (confirm('Are you sure you want to switch back to your original role?')) {
        this.resetToOriginalRole();
      }
    } else {
      // Normal role toggle
      this.toggleRole();
    }
  }

  /**
   * Get the role switcher status message
   */
  protected getRoleSwitcherStatusMessage(): string {
    if (this.roleService.isManualRoleSwitch()) {
      const originalRole = this.roleService.getOriginalRole();
      const currentRole = this.roleService.getCurrentRole();
      return `Switched from ${originalRole} to ${currentRole}`;
    }

    const currentRole = this.roleService.getCurrentRole();
    return `Current role: ${currentRole}`;
  }

  /**
   * Check if the role switcher should show a warning
   */
  protected shouldShowRoleSwitcherWarning(): boolean {
    return this.roleService.isManualRoleSwitch();
  }

  /**
   * Get the role switcher warning message
   */
  protected getRoleSwitcherWarningMessage(): string {
    if (this.roleService.isManualRoleSwitch()) {
      const originalRole = this.roleService.getOriginalRole();
      const currentRole = this.roleService.getCurrentRole();
      return `⚠️ You are currently viewing the system as ${currentRole} (switched from ${originalRole}). This is a temporary view.`;
    }

    return '';
  }

  /**
   * Get the role switcher help text
   */
  protected getRoleSwitcherHelpText(): string {
    if (this.roleService.isManualRoleSwitch()) {
      return 'Click to switch back to your original role. This will clear the temporary vendor view.';
    }

    return 'Click to switch between admin and vendor view modes.';
  }

  /**
   * Get the role switcher button type
   */
  protected getRoleSwitcherButtonType(): string {
    if (this.roleService.isManualRoleSwitch()) {
      return 'warning'; // Show warning style for manual switches
    }

    return 'primary'; // Show primary style for normal switches
  }

  /**
   * Get the role switcher button size
   */
  protected getRoleSwitcherButtonSize(): string {
    if (this.roleService.isManualRoleSwitch()) {
      return 'large'; // Show large size for manual switches to make them more prominent
    }

    return 'medium'; // Show medium size for normal switches
  }

  /**
   * Check if the role switcher button should be disabled
   */
  protected isRoleSwitcherDisabled(): boolean {
    // Disable if no current user or if user is super admin
    if (!this.currentUser || this.currentUser.email === 'system_admin@kormoan.com') {
      return true;
    }

    // Disable if user doesn't have admin role and is not in a manual switch
    if (!this.isAdminUser() && !this.roleService.isManualRoleSwitch()) {
      return true;
    }

    return false;
  }

  /**
   * Check if the role switcher button should show loading state
   */
  protected isRoleSwitcherLoading(): boolean {
    // Show loading if roles are being loaded or if role switching is in progress
    return !this.roleService.getCurrentRole() || this.loadingParking();
  }

  /**
   * Get the role switcher button aria label
   */
  protected getRoleSwitcherAriaLabel(): string {
    if (this.roleService.isManualRoleSwitch()) {
      const originalRole = this.roleService.getOriginalRole();
      const currentRole = this.roleService.getCurrentRole();
      return `Switch back to ${originalRole} role from current ${currentRole} role`;
    }

    const currentRole = this.roleService.getCurrentRole();
    const targetRole = currentRole.toLowerCase() === 'admin' ? 'vendor' : 'admin';
    return `Switch from ${currentRole} role to ${targetRole} role`;
  }

  /**
   * Get the role switcher button title
   */
  protected getRoleSwitcherTitle(): string {
    if (this.roleService.isManualRoleSwitch()) {
      const originalRole = this.roleService.getOriginalRole();
      const currentRole = this.roleService.getCurrentRole();
      return `Click to switch back to ${originalRole} role`;
    }

    const currentRole = this.roleService.getCurrentRole();
    const targetRole = currentRole.toLowerCase() === 'admin' ? 'vendor' : 'admin';
    return `Click to switch to ${targetRole} role`;
  }

  /**
   * Get the role switcher button data attributes
   */
  protected getRoleSwitcherDataAttributes(): any {
    return {
      'data-current-role': this.roleService.getCurrentRole(),
      'data-original-role': this.roleService.getOriginalRole(),
      'data-is-manual-switch': this.roleService.isManualRoleSwitch(),
      'data-can-reset': this.canResetRole(),
      'data-role-switcher': 'true'
    };
  }

  /**
   * Get the role switcher button CSS variables
   */
  protected getRoleSwitcherCssVariables(): any {
    const currentRole = this.roleService.getCurrentRole();
    const isManual = this.roleService.isManualRoleSwitch();

    return {
      '--role-switcher-current-role': currentRole,
      '--role-switcher-is-manual': isManual ? 'true' : 'false',
      '--role-switcher-button-type': this.getRoleSwitcherButtonType(),
      '--role-switcher-button-size': this.getRoleSwitcherButtonSize()
    };
  }

  /**
   * Get the role switcher button styles
   */
  protected getRoleSwitcherStyles(): any {
    const cssVars = this.getRoleSwitcherCssVariables();
    const styles: any = {};

    // Convert CSS variables to inline styles
    Object.keys(cssVars).forEach(key => {
      styles[key] = cssVars[key];
    });

    // Add additional styles based on role
    if (this.roleService.isManualRoleSwitch()) {
      styles['border'] = '2px solid #ff9800';
      styles['background-color'] = '#fff3e0';
    }

    return styles;
  }

  /**
   * Get the role switcher button classes
   */
  protected getRoleSwitcherClasses(): string {
    const classes = ['role-switcher-button'];

    // Add role-specific classes
    const currentRole = this.roleService.getCurrentRole();
    classes.push(`role-switcher-${currentRole.toLowerCase()}`);

    // Add manual switch class if applicable
    if (this.roleService.isManualRoleSwitch()) {
      classes.push('role-switcher-manual');
    }

    // Add button type class
    classes.push(`role-switcher-${this.getRoleSwitcherButtonType()}`);

    // Add button size class
    classes.push(`role-switcher-${this.getRoleSwitcherButtonSize()}`);

    // Add disabled class if applicable
    if (this.isRoleSwitcherDisabled()) {
      classes.push('role-switcher-disabled');
    }

    // Add loading class if applicable
    if (this.isRoleSwitcherLoading()) {
      classes.push('role-switcher-loading');
    }

    return classes.join(' ');
  }

  /**
   * Get the role switcher button attributes
   */
  protected getRoleSwitcherAttributes(): any {
    return {
      'class': this.getRoleSwitcherClasses(),
      'style': this.getRoleSwitcherStyles(),
      'title': this.getRoleSwitcherTitle(),
      'aria-label': this.getRoleSwitcherAriaLabel(),
      'disabled': this.isRoleSwitcherDisabled(),
      'data-loading': this.isRoleSwitcherLoading(),
      ...this.getRoleSwitcherDataAttributes()
    };
  }

  /**
   * Get the role switcher button HTML
   */
  protected getRoleSwitcherHtml(): string {
    const attributes = this.getRoleSwitcherAttributes();
    const buttonText = this.getRoleSwitcherButtonText();
    const icon = this.getRoleSwitcherIcon();

    // Build attributes string
    const attrString = Object.keys(attributes)
      .filter(key => attributes[key] !== undefined && attributes[key] !== null)
      .map(key => {
        if (key === 'style' && typeof attributes[key] === 'object') {
          const styleString = Object.keys(attributes[key])
            .map(styleKey => `${styleKey}: ${attributes[key][styleKey]}`)
            .join('; ');
          return `style="${styleString}"`;
        }
        if (typeof attributes[key] === 'boolean') {
          return attributes[key] ? key : '';
        }
        return `${key}="${attributes[key]}"`;
      })
      .filter(attr => attr.length > 0)
      .join(' ');

    return `<button ${attrString} (click)="handleRoleSwitcherClick()">
      <i class="${icon}"></i>
      <span>${buttonText}</span>
    </button>`;
  }

  protected testRoleSwitcher(): void {
    // Force show role switcher for testing
    if (this.currentUser) {
      // Temporarily set user as admin for testing
      const testUser = { ...this.currentUser, type: 'admin' };

      const userRole = testUser.role_id || testUser.role?.name || testUser.type;
      const normalizedRole = userRole?.toLowerCase();

      // Temporarily override current user for testing
      this.currentUser = testUser;

      // Force refresh the view
      setTimeout(() => {
        this.loadNavigationItems();
      }, 100);
    }
  }

  protected forceRefreshNavigation(): void {
    this.loadNavigationItems();
  }

  protected switchToVendor(): void {
    // Get current URL and query parameters
    const currentUrl = this.router.url;
    const urlParts = currentUrl.split('?');
    const currentPath = urlParts[0];
    const currentQueryParams = urlParts[1] ? this.parseQueryParams(urlParts[1]) : {};

    // Use RoleService to switch role
    this.roleService.switchRole('vendor');

    // Navigate to current path with preserved query parameters
    this.router.navigate([currentPath], {
      queryParams: currentQueryParams
    });
  }

  protected getCurrentRole(): string {
    return this.roleService.getCurrentRole();
  }

  protected toggleRole(): void {
    const currentRole = this.getCurrentRole();
    const originalRole = this.roleService.getOriginalRole();

    // If user is in a manual switch and clicks the switcher, reset to original role
    if (this.roleService.isManualRoleSwitch()) {
      this.resetToOriginalRole();
      return;
    }

    // Otherwise, toggle between admin and vendor
    const newRole = currentRole === 'admin' ? 'vendor' : 'admin';
    // Show loader during transition
    this.roleService.setRoleSwitching(true, 'Switching POV...');

    // Small delay to ensure loader renders before heavy logic
    setTimeout(() => {
      // Use RoleService to switch role
      this.roleService.switchRole(newRole);

      // Handle navigation based on new role
      if (newRole === 'vendor') {
        // When switching to vendor, use vendor context for navigation
        this.switchToVendorNavigation();
      } else {
        // When switching to admin, clear vendor context and navigate with org ID only
        this.switchToAdminNavigation();
        // Clear manual switch flag when switching back to admin
        this.roleService.clearManualSwitchFlag();
      }

      // Hide loader after a short delay (navigation might be async)
      setTimeout(() => {
        this.roleService.setRoleSwitching(false);
      }, 2000); // Increased to 2 seconds for smoother transition

      // Force change detection to update UI elements like parking dropdown
      this.cdr.detectChanges();
    }, 50);
  }

  protected markNotificationAsRead(id: number): void {
    this.mockNotifications.update(notifications =>
      notifications.map(n => n.id === id ? { ...n, isRead: true } : n)
    );
  }

  protected markAllAsRead(): void {
    this.mockNotifications.update(notifications =>
      notifications.map(n => ({ ...n, isRead: true }))
    );
  }

  protected switchToVendorNavigation(): void {
    // Set vendor view mode flag
    this.isVendorViewMode = true;

    // Load parking list to populate dropdown
    this.loadParkingListFromAPI();

    // Preserve current URL and query parameters when switching to vendor role
    const currentUrl = this.getActualCurrentUrl();
    const urlParts = currentUrl.split('?');
    const currentPath = urlParts[0];
    const currentQueryParams = urlParts[1] ? this.parseQueryParams(urlParts[1]) : {};

    // Check if current path is a pov-scoped route (dynamic check)
    const isPovScopedPath = this.isPovScopedRoute(currentPath);

    // Only redirect to dashboard if we're not already on a valid vendor page
    if (!this.isValidAppRoute(currentPath)) {
      this.router.navigate(['/dashboard'], { queryParams: currentQueryParams });
    }

    // Ensure setup status is checked for the new POV
    this.throttledSetupCheck();
  }

  protected switchToAdminNavigation(): void {
    // Clear vendor view mode flag
    this.isVendorViewMode = false;

    // Clear vendor context when switching to admin
    sessionStorage.removeItem('vendorContext');

    // Ensure setup status is checked for the new POV
    this.throttledSetupCheck();

    // Preserve current URL and query parameters when switching to admin role
    const currentUrl = this.getActualCurrentUrl();
    const urlParts = currentUrl.split('?');
    const currentPath = urlParts[0];
    const currentQueryParams = urlParts[1] ? this.parseQueryParams(urlParts[1]) : {};

    // Only redirect to dashboard if we're not already on a valid admin page
    if (!this.isValidAppRoute(currentPath)) {
      this.router.navigate(['/dashboard'], { queryParams: currentQueryParams });
    }
  }

  protected navigateToDashboard(): void {
    // Use navigation service to get the appropriate route based on role
    const navigationId = this.navigationService.getRoleBasedNavigationId();
    const currentRole = this.roleService.getCurrentRole();
    const isVendorMode = this.isVendorViewMode || currentRole === 'vendor';

    if (navigationId) {
      if (isVendorMode) {
        // Use new URL format for vendor: /parking/:parkingId/dashboard
        this.router.navigate(['/parking', navigationId, 'dashboard']);
      } else {
        // For admin role, use /:pov/:orgId/dashboard format
        const userPov = this.getCachedStorageItem('user_pov', false, true);
        const urlPrefix = userPov ? userPov.toLowerCase() : 'organization';
        this.router.navigate([urlPrefix, navigationId, 'dashboard']);
      }
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  protected navigateToSettings(): void {
    const currentRole = this.roleService.getCurrentRole();

    // Super admin: navigate directly to first settings item route (no org-scoped URL)
    if (currentRole === 'super admin') {
      const settingsItems = this.navigationService.getSettingsNavigationItems();
      if (settingsItems && settingsItems.length > 0) {
        this.router.navigate([settingsItems[0].route]);
      } else {
        this.router.navigate(['/settings/regions']);
      }
      return;
    }

    // Get current URL to check if we're in /:pov/:orgId/ format (dynamic pov routes)
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/').filter(seg => seg.length > 0);

    // Check if URL matches /:pov/:orgId/ pattern (where second segment is UUID)
    let isPovScopedRoute = false;
    let orgIdFromUrl: string | null = null;
    let povFromUrl: string | null = null;

    if (segments.length >= 2) {
      const firstSegment = segments[0];
      const secondSegment = segments[1];
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const knownRoutes = ['login', 'signup', 'dashboard', 'settings', 'parkings', 'parking-lots', 'customers', 'reports', 'session', 'passes', 'facilities', 'reservations', 'team', 'billing', 'subscriptions', 'plans', 'transactions', 'organisations', 'admin-dashboard', 'live-dashboard', 'ai', 'sistem-ai-insight', 'vehicle-sessions', 'linked-vehicles', 'data-exports', 'data-export', 'dataexport', 'data-import', 'dataimport', 'import-historical-data', 'export-historical-data', 'facility-usage-logs', 'customer', 'reservation', 'parking', 'parking-add-people', 'parking-add-capacity', 'review-parking', 'subscriptions-plans', 'parking-profile', 'profile', 'upgrade-plan', 'billing-invoice', 'payment-gateway', 'payment-options', 'taxes', 'organization-profile', 'org-profile', 'media-branding', 'passes-details', 'report-view', 'facility-activities', 'setup-guide', 'timezone-settings', 'timezone-demo', 'global-settings', 'role-navigation-demo', 'role-switcher-debug', 'role-switcher-test', 'not-found', 'network-error'];

      // If first segment is not a known route and second segment is a UUID, it's a pov-scoped route
      if (!knownRoutes.includes(firstSegment) && uuidPattern.test(secondSegment)) {
        isPovScopedRoute = true;
        orgIdFromUrl = secondSegment;
        povFromUrl = firstSegment;
      }
    }

    // Also check for /org/ format for backward compatibility
    const isOrgScopedRoute = currentUrl.startsWith('/org/') || isPovScopedRoute;

    // Get organization ID
    const organizationId = orgIdFromUrl || this.apiService.getCurrentOrganizationId() || sessionStorage.getItem('organization_id');

    // Get settings navigation items
    const settingsItems = this.navigationService.getSettingsNavigationItems();

    // If there are settings items available, navigate to the first one
    if (settingsItems && settingsItems.length > 0) {
      const firstSettingsItem = settingsItems[0];
      let route = firstSettingsItem.route;

      // Handle routes that start with /settings/ (e.g., /settings/org-profile)
      let subRoute = route;
      if (route.startsWith('/settings/')) {
        subRoute = route.substring('/settings/'.length);
      } else if (route.startsWith('settings/')) {
        subRoute = route.substring('settings/'.length);
      } else if (route.startsWith('/')) {
        subRoute = route.substring(1);
      }

      // Get user_pov value for URL prefix
      const userPov = sessionStorage.getItem('user_pov');
      const urlPrefix = userPov ? userPov.toLowerCase() : 'organization';

      // Check if user_pov is "parking" - use parkingId instead of organizationId
      if (userPov && userPov.toLowerCase() === 'parking') {
        const parkingId = this.getCachedStorageItem('parking_id', false, true);
        if (parkingId) {
          // Use dynamic prefix from user_pov
          const urlPrefix = userPov.toLowerCase();
          this.router.navigate([urlPrefix, parkingId, 'settings', subRoute]);
          return;
        }
      }

      // For admin/super admin with organization ID, use /:pov/:orgId/settings/:subRoute format
      if ((currentRole === 'admin' || currentRole === 'super admin') && organizationId) {
        // If we're on a pov-scoped route, use pov format
        if (isPovScopedRoute && orgIdFromUrl) {
          // Use the pov from URL if available, otherwise use user_pov from localStorage
          const finalUrlPrefix = povFromUrl || urlPrefix;
          this.router.navigate([finalUrlPrefix, organizationId, 'settings', subRoute]);
          return;
        }
        // Also use pov format if we have orgId (even if not currently on pov route)
        this.router.navigate([urlPrefix, organizationId, 'settings', subRoute]);
        return;
      }

      // Use navigation service to get the appropriate route based on role
      const navigationId = this.navigationService.getRoleBasedNavigationId();

      if (navigationId) {
        // Navigate to the first settings item with navigation ID
        this.router.navigate([firstSettingsItem.route, navigationId]);
      } else {
        // Navigate to the first settings item without navigation ID
        this.router.navigate([firstSettingsItem.route]);
      }
    } else {
      // Fallback: Use navigation service to get the appropriate route based on role
      const navigationId = this.navigationService.getRoleBasedNavigationId();

      // Get user_pov value for URL prefix
      const userPov = sessionStorage.getItem('user_pov');
      const urlPrefix = userPov ? userPov.toLowerCase() : 'organization';

      // Check if user_pov is "parking" - use parkingId instead of organizationId
      if (userPov && userPov.toLowerCase() === 'parking') {
        const parkingId = sessionStorage.getItem('parking_id');
        if (parkingId) {
          const urlPrefix = userPov.toLowerCase();
          // Navigate to parking-profile as the default first tab
          this.router.navigate([urlPrefix, parkingId, 'settings', 'parking-profile']);
          return;
        }
      }

      // For admin/super admin with organization ID, use /:pov/:orgId/settings/org-profile format
      if ((currentRole === 'admin' || currentRole === 'super admin') && organizationId) {
        if (isPovScopedRoute && orgIdFromUrl) {
          const finalUrlPrefix = povFromUrl || urlPrefix;
          this.router.navigate([finalUrlPrefix, organizationId, 'settings', 'org-profile']);
          return;
        }
        this.router.navigate([urlPrefix, organizationId, 'settings', 'org-profile']);
        return;
      }

      if (navigationId) {
        this.router.navigate(['/settings', navigationId]);
      } else {
        this.router.navigate(['/settings']);
      }
    }
  }

  protected getRoleName(): string {
    // First, check if the user is actually a super admin based on their data
    // This check should happen BEFORE checking currentRole to ensure super admin is always detected
    const isSuperAdmin = this.currentUser?.email === 'system_admin@kormoan.com' ||
      this.currentUser?.role?.name?.toLowerCase() === 'super admin' ||
      this.currentUser?.type?.toLowerCase() === 'super admin' ||
      this.currentUser?.role_id?.toLowerCase()?.includes('super admin');

    if (isSuperAdmin) {
      return 'Super Admin';
    }

    // Get current role from RoleService
    const currentRole = this.roleService.getCurrentRole();

    // Check current role for super admin
    if (currentRole && currentRole.toLowerCase() === 'super admin') {
      return 'Super Admin';
    }

    // If not super admin, use the current role from service
    if (currentRole) {
      // Convert role name to display format
      const displayName = currentRole.charAt(0).toUpperCase() + currentRole.slice(1);
      return displayName;
    }

    // Fallback to normalizeRole for consistent role determination
    const normalizedRole = this.roleService.normalizeRole({
      roleId: this.currentUser?.role_id,
      roleName: this.currentUser?.role?.name,
      type: this.currentUser?.type,
      email: this.currentUser?.email
    });

    // Special handling for super admin display
    if (normalizedRole.toLowerCase() === 'super admin') {
      return 'Super Admin';
    }

    // Convert role name to display format
    const displayName = normalizedRole.charAt(0).toUpperCase() + normalizedRole.slice(1);
    return displayName;
  }

  protected getRoleNameFromAPI(): string {
    return this.roleService.getDisplayRoleName({
      roleId: this.currentUser?.role_id,
      roleName: this.currentUser?.role?.name,
      type: this.currentUser?.type,
      email: this.currentUser?.email
    });
  }

  private parseQueryParams(queryString: string): any {
    const params: any = {};
    const pairs = queryString.split('&');

    for (const pair of pairs) {
      const [key, value] = pair.split('=');
      if (key && value) {
        params[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    }

    return params;
  }

  /**
   * Navigate to a route while preserving current route parameters
   */
  protected navigateWithPreservedParams(route: string): void {
    // Special handling for routes that can work without POV - allow navigation without POV if no context available
    const routesWithoutPov = [
      '/dashboard', '/billing', '/settings/regions', '/settings/language',
      '/settings/payment-gateway', '/settings/devices', '/settings/thermal-printer',
      '/organisations', '/admin-dashboard', '/subscribers', '/transactions', '/sub-price', '/plan-price-group', '/plans'
    ];
    if (routesWithoutPov.includes(route)) {
      const userPov = sessionStorage.getItem('user_pov');
      const parkingId = sessionStorage.getItem('parking_id');
      const organizationId = this.getCachedStorageItem('organization_id', false, true);

      // If no POV context, navigate to simple route
      if (!userPov && !parkingId && !organizationId) {
        this.router.navigateByUrl(route);
        return;
      }
    }

    // Get current URL to check if we're in /:pov/:orgId/ format (dynamic pov routes)
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/').filter(seg => seg.length > 0);

    // Check if URL matches /:pov/:orgId/ pattern (where second segment is UUID)
    let isPovScopedRoute = false;
    let orgIdFromUrl: string | null = null;
    let povFromUrl: string | null = null;

    if (segments.length >= 2) {
      const firstSegment = segments[0];
      const secondSegment = segments[1];
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const knownRoutes = ['login', 'signup', 'dashboard', 'settings', 'parkings', 'parking-lots', 'customers', 'reports', 'session', 'passes', 'facilities', 'reservations', 'team', 'billing', 'subscriptions', 'plans', 'transactions', 'organisations', 'admin-dashboard', 'live-dashboard', 'ai', 'sistem-ai-insight', 'vehicle-sessions', 'linked-vehicles', 'data-exports', 'data-export', 'dataexport', 'data-import', 'dataimport', 'import-historical-data', 'export-historical-data', 'facility-usage-logs', 'customer', 'reservation', 'parking', 'parking-add-people', 'parking-add-capacity', 'review-parking', 'subscriptions-plans', 'parking-profile', 'profile', 'upgrade-plan', 'billing-invoice', 'payment-gateway', 'payment-options', 'taxes', 'organization-profile', 'org-profile', 'media-branding', 'passes-details', 'report-view', 'facility-activities', 'setup-guide', 'timezone-settings', 'timezone-demo', 'global-settings', 'role-navigation-demo', 'role-switcher-debug', 'role-switcher-test', 'not-found', 'network-error'];

      // If first segment is not a known route and second segment is a UUID, it's a pov-scoped route
      if (!knownRoutes.includes(firstSegment) && uuidPattern.test(secondSegment)) {
        isPovScopedRoute = true;
        orgIdFromUrl = secondSegment;
        povFromUrl = firstSegment;
      }
    }

    // Also check for /org/ format for backward compatibility
    const isOrgScopedRoute = currentUrl.startsWith('/org/') || isPovScopedRoute;

    // Get organization ID from localStorage
    const organizationId = orgIdFromUrl || sessionStorage.getItem('organization_id');

    // Get current role from RoleService
    const currentRole = this.roleService.getCurrentRole();
    const isVendor = currentRole === 'vendor';

    // Get user_pov value for URL prefix - CHECK THIS FIRST before vendor role
    const userPov = sessionStorage.getItem('user_pov');
    const urlPrefix = userPov ? userPov.toLowerCase() : 'organization';

    // Check if user_pov is "parking" - use parkingId instead of organizationId
    // This MUST come FIRST before vendor role checks to ensure correct POV format
    if (userPov && userPov.toLowerCase() === 'parking') {
      const parkingId = sessionStorage.getItem('parking_id');
      if (parkingId) {
        // Get dynamic prefix from user_pov
        const urlPrefix = userPov.toLowerCase();

        // Handle settings routes - check if route is a settings sub-route
        const settingsSubRoutes = ['org-profile', 'organization-profile', 'parking-profile', 'team', 'passes', 'facilities', 'integrations', 'devices', 'payment-gateway', 'payment-options', 'taxes', 'regions', 'language', 'thermal-printer', 'media-branding', 'vehicle-rate', 'vehicle-rates', 'pass-rates', 'facility-rates', 'facility-setup', 'hardware-integration', 'anpr-module', 'import-historical-data', 'export-historical-data', 'data-export', 'data-import'];

        // If route starts with /settings/, extract sub-route
        if (route.startsWith('/settings/')) {
          const subRoute = route.substring('/settings/'.length);
          const absolutePath = `/${urlPrefix}/${parkingId}/settings/${subRoute}`;
          this.router.navigateByUrl(absolutePath);
          return;
        }

        // Check if route is a settings sub-route name (without /settings/ prefix)
        // But exclude routes that are also regular routes (like 'passes')
        const regularRoutes = ['passes', 'session', 'vehicle-sessions', 'customers', 'reports', 'reservations', 'facilities', 'dashboard', 'billing', 'ai'];
        let routeName = route.startsWith('/') ? route.substring(1) : route;

        // Only treat as settings sub-route if it's in settingsSubRoutes AND not a regular route
        if (settingsSubRoutes.includes(routeName) && !regularRoutes.includes(routeName)) {
          // Navigate to /:user_pov/:parkingId/settings/:subRoute
          const absolutePath = `/${urlPrefix}/${parkingId}/settings/${routeName}`;
          this.router.navigateByUrl(absolutePath);
          return;
        }

        // Remove leading slash from route if present
        const cleanRoute = route.startsWith('/') ? route.substring(1) : route;
        // Navigate to /:user_pov/:parkingId/:pageName
        const absolutePath = `/${urlPrefix}/${parkingId}/${cleanRoute}`;
        this.router.navigateByUrl(absolutePath);
        return;
      }
    }

    // For organization POV, use organizationId
    if (organizationId) {
      // Handle settings routes - ALWAYS use pov-scoped format when we have organizationId
      const settingsSubRoutes = ['org-profile', 'organization-profile', 'parking-profile', 'team', 'passes', 'facilities', 'integrations', 'devices', 'payment-gateway', 'taxes', 'regions', 'language', 'thermal-printer', 'media-branding', 'vehicle-rate', 'vehicle-rates', 'pass-rates', 'facility-rates', 'facility-setup', 'hardware-integration', 'anpr-module', 'payment-options', 'import-historical-data', 'export-historical-data', 'data-export', 'data-import'];

      // Check if route is a settings route
      if (route.startsWith('/settings/')) {
        const subRoute = route.substring('/settings/'.length);
        // Always use pov format for settings routes
        const finalUrlPrefix = (isPovScopedRoute && povFromUrl) ? povFromUrl : urlPrefix;
        const absolutePath = `/${finalUrlPrefix}/${organizationId}/settings/${subRoute}`;
        this.router.navigateByUrl(absolutePath);
        return;
      }

      // Check if route is a settings sub-route name (without /settings/ prefix)
      // But exclude routes that are also regular routes (like 'passes')
      const regularRoutes = ['passes', 'session', 'vehicle-sessions', 'customers', 'reports', 'reservations', 'facilities', 'dashboard', 'billing', 'ai'];
      let routeName = route.startsWith('/') ? route.substring(1) : route;

      // Only treat as settings sub-route if it's in settingsSubRoutes AND not a regular route
      if (settingsSubRoutes.includes(routeName) && !regularRoutes.includes(routeName)) {
        // Always use pov format for settings routes
        const finalUrlPrefix = (isPovScopedRoute && povFromUrl) ? povFromUrl : urlPrefix;
        const absolutePath = `/${finalUrlPrefix}/${organizationId}/settings/${routeName}`;
        this.router.navigateByUrl(absolutePath);
        return;
      }

      // If current route is in /:pov/:orgId/ format, use /:pov/:orgId/:pageName format
      if (isPovScopedRoute && orgIdFromUrl) {
        // Use the pov from URL if available, otherwise use user_pov from localStorage
        const finalUrlPrefix = povFromUrl || urlPrefix;
        // Remove leading slash from route if present
        const cleanRoute = route.startsWith('/') ? route.substring(1) : route;
        // Navigate to /:pov/:orgId/:pageName
        const absolutePath = `/${finalUrlPrefix}/${organizationId}/${cleanRoute}`;
        this.router.navigateByUrl(absolutePath);
        return;
      }

      // For admin/super admin, always use pov format when we have organizationId
      const currentRole = this.roleService.getCurrentRole();
      if ((currentRole === 'admin' || currentRole === 'super admin') && organizationId) {
        const cleanRoute = route.startsWith('/') ? route.substring(1) : route;
        const absolutePath = `/${urlPrefix}/${organizationId}/${cleanRoute}`;
        this.router.navigateByUrl(absolutePath);
        return;
      }

      // Otherwise use the route as is (assuming it's already properly formatted or absolute)
      this.router.navigateByUrl(route);
      return;
    }

    // Check if we have vendor context in localStorage (fallback if not parking POV)
    // Reuse isVendor declared earlier at line 1079
    const vendorContext = sessionStorage.getItem('vendorContext');
    let vendorData = null;

    if (vendorContext) {
      try {
        vendorData = JSON.parse(vendorContext);
      } catch (e) {
        // Error parsing vendor context
      }
    }

    // If vendor role and we have vendor context, use it for navigation (only parkingId, no orgId)
    // This is a fallback if user_pov is not "parking"
    // Reuse isVendor declared earlier at line 1079
    // BUT: Skip this if the route is already an absolute POV-scoped path (like /organization/ID/dashboard)
    const isAbsolutePovRoute = route.startsWith('/') && (route.includes('/organization/') || route.includes('/parking/') || route.split('/').length > 2);

    if (isVendor && vendorData && (!userPov || userPov.toLowerCase() !== 'parking') && !isAbsolutePovRoute) {
      const navigationParams = [route, vendorData.parkingId];

      this.router.navigate(navigationParams).then((success) => {
        // Navigation successful
      }).catch((error) => {
        // Navigation error
      });
      return;
    }

    // If vendor role but no context, try to get from current user (only parkingId, no orgId)
    // This is a fallback if user_pov is not "parking"
    // Reuse isVendor declared earlier at line 1079
    // BUT: Skip this if the route is already an absolute POV-scoped path
    if (isVendor && (!userPov || userPov.toLowerCase() !== 'parking') && !isAbsolutePovRoute) {
      const currentUser = this.apiService.getCurrentUser();

      // Check for parkingId directly in user object
      if (currentUser?.parkingId) {
        this.router.navigate([route, currentUser.parkingId]);
        return;
      }
      // Check for parking object with id
      else if (currentUser?.parking?.id) {
        this.router.navigate([route, currentUser.parking.id]);
        return;
      }
      // Check for parkings array (fallback)
      else if (currentUser?.parkings?.length > 0) {
        const parkingId = currentUser.parkings[0].id;
        this.router.navigate([route, parkingId]);
        return;
      } else {
        // Vendor role but no parking data - navigate without parameters to preserve vendor role
        this.router.navigate([route]);
        return;
      }
    }

    this.router.navigateByUrl(route);
  }

  /**
   * Get dynamic routerLink array for navigation items
   * Generates the correct URL based on user_pov
   */
  protected getDynamicRouterLink(route: string): string[] {
    let userPov: string | null = null;
    let parkingId: string | null = null;
    let organizationId: string | null = null;

    // HIGHEST PRIORITY: Extract from current URL to ensure context consistency
    const currentUrl = this.router.url;
    const urlSegments = currentUrl.split('/').filter(seg => seg.length > 0);
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const povSegments = ['active', 'admin', 'organization', 'parking', 'vendor', 'org'];

    if (urlSegments.length >= 2 && povSegments.includes(urlSegments[0].toLowerCase()) && uuidPattern.test(urlSegments[1])) {
      const urlPov = urlSegments[0].toLowerCase();
      const urlId = urlSegments[1];

      // Always prioritize URL context over storage to prevent stale navigation
      userPov = urlPov;
      if (urlPov === 'parking' || urlPov === 'vendor') {
        parkingId = urlId;
      } else {
        organizationId = urlId;
      }
    }

    // FALLBACK: If URL doesn't have context, use storage
    if (!userPov) {
      userPov = this.getCachedStorageItem('user_pov', false, true);
    }
    if (!parkingId) {
      parkingId = this.getCachedStorageItem('parking_id', false, true);
    }
    if (!organizationId) {
      organizationId = this.apiService.getCurrentOrganizationId() || this.getCachedStorageItem('organization_id', false, true);
    }

    // FINAL FALLBACK: From user_info in localStorage
    if (!userPov || (!organizationId && !parkingId)) {
      const userInfoStr = this.getCachedStorageItem('user_info');
      if (userInfoStr) {
        try {
          const userInfo = JSON.parse(userInfoStr);
          if (!userPov) userPov = userInfo.user_pov || (userInfo.type === 'admin' ? 'organization' : 'parking');
          if (!organizationId) organizationId = userInfo.organization_id || userInfo.org_id;
          if (!parkingId) parkingId = userInfo.parking_id || userInfo.parkingId;
        } catch (e) {
          // Ignore parse errors
        }
      }
    }

    const rawUrlPrefix = userPov ? userPov.toLowerCase() : 'organization';
    const urlPrefix = '/' + rawUrlPrefix; // Ensure absolute path from root

    // console.log(`[getDynamicRouterLink] route=${route}, userPov=${userPov}, orgId=${organizationId}, parkingId=${parkingId}`);

    // Resolve active parking ID for parking-scoped operations like POS
    const activeParkingId = parkingId ||
      this.selectedParkingId ||
      this.parkingControl?.value?.id ||
      sessionStorage.getItem('selectedParkingId') ||
      sessionStorage.getItem('parking_id') ||
      localStorage.getItem('parking_id') ||
      this.apiService.getCurrentParkingId() ||
      (this.parkingList && this.parkingList.length > 0 ? this.parkingList[0]?.id : null);

    // Use a more stable cache key including all inputs (including role to differentiate super admin)
    const cacheRole = this.roleService.getCurrentRole();
    const resultKey = `${route}_${userPov}_${parkingId}_${activeParkingId}_${organizationId}_${cacheRole}`;
    const cached = this.routerLinkCache.get(resultKey);

    if (cached) {
      return cached;
    }

    // Identify routes that should always be root-level (global admin routes)
    // These routes do not support POV-scoped prefixes (like /organization/ID/)
    const globalAdminRoutes = ['organisations', 'admin-dashboard', 'subscribers', 'transactions', 'sub-price', 'plan-price-group', 'plans', 'help-center/activities'];
    const cleanRouteName = route.startsWith('/') ? route.substring(1) : route;

    // Handle settings and POS routes
    let result: string[] = [];
    if (cleanRouteName === 'pos' || cleanRouteName === 'pos-overview' || cleanRouteName === 'pos-dashboard') {
      if (activeParkingId) {
        result = ['/parking', activeParkingId, 'pos-overview'];
      } else {
        result = ['/pos-overview'];
      }
    } else if (cleanRouteName === 'pos-session') {
      if (activeParkingId) {
        result = ['/parking', activeParkingId, 'pos-session'];
      } else {
        result = ['/pos-session'];
      }
    } else if (cleanRouteName === 'pos-passes') {
      if (activeParkingId) {
        result = ['/parking', activeParkingId, 'pos-passes'];
      } else {
        result = ['/pos-passes'];
      }
    } else if (cleanRouteName === 'pos-facilities') {
      if (activeParkingId) {
        result = ['/parking', activeParkingId, 'pos-facilities'];
      } else {
        result = ['/pos-facilities'];
      }
    } else if (cleanRouteName === 'facility-usage-logs' || cleanRouteName === 'facility-activities') {
      if (activeParkingId) {
        result = ['/parking', activeParkingId, cleanRouteName];
      } else if (organizationId) {
        result = [urlPrefix, organizationId, cleanRouteName];
      } else {
        result = ['/' + cleanRouteName];
      }
    } else if (cleanRouteName === 'vehicle-sessions') {
      if (userPov && userPov.toLowerCase() === 'parking' && parkingId) {
        result = [urlPrefix, parkingId, 'vehicle-sessions'];
      } else if (activeParkingId) {
        result = ['/parking', activeParkingId, 'vehicle-sessions'];
      } else if (organizationId) {
        result = [urlPrefix, organizationId, 'vehicle-sessions'];
      } else {
        result = ['/vehicle-sessions'];
      }
    } else if (cleanRouteName === 'profile') {
      if (userPov && userPov.toLowerCase() === 'parking' && parkingId) {
        result = ['/parking', parkingId, 'profile'];
      } else if (organizationId) {
        result = [urlPrefix, organizationId, 'profile'];
      } else if (activeParkingId) {
        result = ['/parking', activeParkingId, 'profile'];
      } else {
        result = ['/profile'];
      }
    } else if (cleanRouteName === 'settings') {
      const currentRole = this.roleService.getCurrentRole();
      // Super admin: navigate directly to first settings item route
      if (currentRole === 'super admin') {
        const settingsItems = this.navigationService.getSettingsNavigationItems();
        const firstRoute = (settingsItems && settingsItems.length > 0) ? settingsItems[0].route : '/settings/regions';
        result = [firstRoute.startsWith('/') ? firstRoute : `/${firstRoute}`];
      } else {
        const subRoute = (userPov && userPov.toLowerCase() === 'parking') ? 'parking-profile' : 'org-profile';
        if (userPov && userPov.toLowerCase() === 'parking' && parkingId) {
          result = [urlPrefix, parkingId, 'settings', subRoute];
        } else if (organizationId) {
          result = [urlPrefix, organizationId, 'settings', subRoute];
        } else {
          result = ['/settings', subRoute];
        }
      }
    } else if (globalAdminRoutes.includes(cleanRouteName)) {
      // Global admin routes should always be absolute from root
      result = [route.startsWith('/') ? route : '/' + route];
    } else if (route.startsWith('/settings/')) {
      const currentRoleForSettings = this.roleService.getCurrentRole();
      // Super admin: always use direct /settings/... route
      if (currentRoleForSettings === 'super admin') {
        result = [route.startsWith('/') ? route : '/' + route];
      } else {
        const subRoute = route.substring('/settings/'.length);
        if (userPov && userPov.toLowerCase() === 'parking' && parkingId) {
          result = [urlPrefix, parkingId, 'settings', subRoute];
        } else if (organizationId) {
          result = [urlPrefix, organizationId, 'settings', subRoute];
        } else {
          result = [route.startsWith('/') ? route : '/' + route];
        }
      }
    } else {
      // Handle regular routes
      const routeName = route.startsWith('/') ? route.substring(1) : route;
      if (userPov && userPov.toLowerCase() === 'parking' && parkingId) {
        result = [urlPrefix, parkingId, routeName];
      } else if (organizationId) {
        result = [urlPrefix, organizationId, routeName];
      } else {
        result = [route.startsWith('/') ? route : '/' + route];
      }
    }

    this.routerLinkCache.set(resultKey, result);
    // console.log(`[SIDEBAR CACHE] Created link for ${route}: ${JSON.stringify(result)}`);
    return result;
  }

  /**
   * Check if a navigation route is currently active
   * Handles both dynamic POV routes and regular routes
   */
  protected isRouteActive(route: string): boolean {
    // Read signal to create dependency - this triggers change detection when signal updates
    const signalValue = this.currentUrl();
    // Always use router.url as fallback to ensure we have current URL
    const currentUrl = this.router.url;
    const urlWithoutQuery = currentUrl.split('?')[0]; // Remove query params

    if (!route || !urlWithoutQuery) return false;

    // Remove leading slash from route for comparison
    const routeName = route.startsWith('/') ? route.substring(1) : route;

    // Extract segments from current URL
    const urlSegments = urlWithoutQuery.split('/').filter(seg => seg.length > 0);

    // Special check for base '/settings' route: active if URL contains '/settings'
    if (route === '/settings' || routeName === 'settings') {
      return urlSegments.includes('settings');
    }

    // Special check for POS: active if URL contains '/pos-overview', '/pos-dashboard', '/pos-session' or '/pos'
    if (routeName === 'pos' || routeName === 'pos-overview' || routeName === 'pos-dashboard') {
      return urlWithoutQuery.includes('/pos-overview') || urlWithoutQuery.includes('/pos-dashboard') || urlWithoutQuery.includes('/pos-session') || urlWithoutQuery.endsWith('/pos');
    }
    if (routeName === 'pos-session') {
      return urlWithoutQuery.includes('/pos-session');
    }

    // Helper to strip trailing 's' for singular/plural matching
    const normalize = (s: string) => s.toLowerCase().replace(/s$/, '');

    // Handle settings routes specially
    if (route.startsWith('/settings/')) {
      const settingsSubRoute = route.substring('/settings/'.length);

      // Check if URL is in POV format with settings: /:pov/:id/settings/:subRoute
      if (urlSegments.length >= 4 && urlSegments[urlSegments.length - 2] === 'settings') {
        const currentSettingsSubRoute = urlSegments[urlSegments.length - 1];
        const isActive = normalize(currentSettingsSubRoute) === normalize(settingsSubRoute);
        return isActive;
      }

      // Check old format: /settings/:subRoute
      if (urlSegments.length >= 2 && urlSegments[0] === 'settings') {
        const currentSettingsSubRoute = urlSegments[1];
        const isActive = normalize(currentSettingsSubRoute) === normalize(settingsSubRoute);
        return isActive;
      }

      return urlWithoutQuery.toLowerCase().includes(route.toLowerCase());
    }

    // Check if URL is in POV format: /:pov/:id/:pageName
    // Example: /parking/123/dashboard should match route /dashboard
    if (urlSegments.length >= 3) {
      const pageName = urlSegments[urlSegments.length - 1];
      // Compare last segment with route name
      if (normalize(pageName) === normalize(routeName)) {
        return true;
      }
    }

    // Check if URL is in old format: /:pageName/:id
    // Example: /dashboard/123 should match route /dashboard
    if (urlSegments.length >= 2) {
      const pageName = urlSegments[0];
      if (normalize(pageName) === normalize(routeName)) {
        return true;
      }
    }

    // Check exact match for old format routes
    const routePattern = route.startsWith('/') ? route : `/${route}`;
    if (urlWithoutQuery === routePattern || urlWithoutQuery.toLowerCase().includes(routePattern.toLowerCase())) {
      return true;
    }

    return false;
  }

  /**
   * Check if URL is a pov-scoped route (/:pov/:id/...)
   */
  private isPovScopedRoute(url: string): boolean {
    const segments = url.split('/').filter(seg => seg.length > 0);
    if (segments.length >= 2) {
      const firstSegment = segments[0];
      const secondSegment = segments[1];
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const knownRoutes = ['login', 'signup', 'dashboard', 'settings', 'parkings', 'parking-lots', 'customers', 'reports', 'session', 'passes', 'facilities', 'reservations', 'team', 'billing', 'subscriptions', 'plans', 'transactions', 'organisations', 'admin-dashboard', 'live-dashboard', 'ai', 'sistem-ai-insight', 'vehicle-sessions', 'linked-vehicles', 'data-exports', 'data-export', 'dataexport', 'data-import', 'dataimport', 'import-historical-data', 'export-historical-data', 'facility-usage-logs', 'customer', 'reservation', 'parking', 'parking-add-people', 'parking-add-capacity', 'review-parking', 'subscriptions-plans', 'parking-profile', 'profile', 'upgrade-plan', 'billing-invoice', 'payment-gateway', 'payment-options', 'taxes', 'organization-profile', 'org-profile', 'media-branding', 'passes-details', 'report-view', 'facility-activities', 'setup-guide', 'timezone-settings', 'timezone-demo', 'global-settings', 'role-navigation-demo', 'role-switcher-debug', 'role-switcher-test', 'not-found', 'network-error'];

      // If first segment is not a known route and second segment is a UUID, it's a pov-scoped route
      if (!knownRoutes.includes(firstSegment) && uuidPattern.test(secondSegment)) {
        return true;
      }
    }
    // Also check for /parking/ and /org/ for backward compatibility
    return url.startsWith('/parking/') || url.startsWith('/org/');
  }

  /**
   * Check if URL matches POV settings route pattern: /:pov/:id/settings/:page_name
   * Example: /Povs/123-456-789/settings/team
   */
  private isPovSettingsRoute(url: string): boolean {
    const segments = url.split('/').filter(seg => seg.length > 0);

    // Check if URL matches pattern: /:pov/:id/settings/:page_name (4 segments minimum)
    if (segments.length >= 4) {
      const firstSegment = segments[0]; // POV (e.g., "Povs")
      const secondSegment = segments[1]; // ID (UUID)
      const thirdSegment = segments[2]; // Should be "settings"

      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

      // Check if second segment is UUID and third segment is "settings"
      if (uuidPattern.test(secondSegment) && thirdSegment === 'settings') {
        return true;
      }
    }

    return false;
  }

  /**
   * Helper to get the actual current URL during initial page load/hard refresh when router.url is '/'
   */
  private getActualCurrentUrl(): string {
    const routerUrl = this.router.url;
    if (routerUrl && routerUrl !== '/') {
      return routerUrl;
    }
    if (typeof window !== 'undefined' && window.location && window.location.pathname && window.location.pathname !== '/') {
      return window.location.pathname + (window.location.search || '') + (window.location.hash || '');
    }
    return routerUrl || '/';
  }

  /**
   * Check if current route path is a valid application route that should be preserved on refresh or role switch
   */
  private isValidAppRoute(url: string): boolean {
    if (!url || url === '/' || url === '/login' || url === '/signup') {
      return false;
    }
    const cleanPath = url.split('?')[0];
    if (this.isPovScopedRoute(cleanPath)) {
      return true;
    }
    const validRoutePrefixes = [
      '/dashboard', '/settings', '/parkings', '/parking-lots', '/customers', '/customer',
      '/reports', '/report', '/report-view', '/session', '/vehicle-sessions', '/passes',
      '/facilities', '/facility-activities', /* '/reservations', */ '/team', '/billing',
      '/billing-invoice', '/subscriptions', '/plans', '/transactions', '/organisations',
      '/admin-dashboard', '/live-dashboard', '/ai', '/sistem-ai-insight', '/linked-vehicles',
      '/data-exports', '/data-export', '/dataexport', '/data-import', '/dataimport', '/import-historical-data', '/export-historical-data', '/facility-usage-logs', '/parking-add-people', '/parking-add-capacity',
      '/review-parking', '/subscriptions-plans', '/parking-profile', '/profile', '/upgrade-plan',
      '/payment-gateway', '/payment-options', '/taxes', '/organization-profile', '/org-profile',
      '/media-branding', '/passes-details', '/setup-guide', '/timezone-settings', '/global-settings',
      '/pos', '/no-parking', '/no-organization'
    ];
    return validRoutePrefixes.some(prefix => cleanPath.startsWith(prefix) || cleanPath.includes(prefix));
  }

  /**
   * Extract route parameters from current URL
   */
  private extractRouteParamsFromUrl(url: string): { parkingId?: string, parkingName?: string, organizationId?: string } {
    // Remove query parameters from URL
    const pathOnly = url.split('?')[0];
    const segments = pathOnly.split('/').filter(segment => segment.length > 0);

    // Check if URL format is /route/orgId/parkingId/parkingName (vendor format)
    if (segments.length >= 4) {
      const orgId = segments[segments.length - 3];
      const parkingId = segments[segments.length - 2];
      const parkingName = segments[segments.length - 1];

      // Validate parkingId (should be UUID format)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const isValidUuid = uuidRegex.test(parkingId);

      if (isValidUuid) {
        return { organizationId: orgId, parkingId, parkingName };
      }
    }

    // Check if URL format is /route/parkingId/parkingName (old format)
    if (segments.length >= 3) {
      const parkingId = segments[segments.length - 2];
      const parkingName = segments[segments.length - 1];

      // Validate parkingId (should be UUID format)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(parkingId)) {
        return { parkingId, parkingName };
      }
    }

    return {};
  }

  /**
   * Get current query parameters
   */
  protected getCurrentQueryParams(): any {
    const currentUrl = this.router.url;
    const urlParts = currentUrl.split('?');
    return urlParts[1] ? this.parseQueryParams(urlParts[1]) : {};
  }

  // Format parking name for URL
  private formatParkingNameForUrl(parkingName: string): string {
    if (!parkingName) return '';
    return parkingName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim();
  }

  protected handleToggle(): void {
    if (this.isPhoneLayout()) {
      this.closeMobileNav();
      return;
    }

    this.expanded.update(v => !v);

    // Dispatch window resize event to trigger responsive component recalculations
    // Small delay to allow layout transition to complete
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 200);
  }

  protected toggleMobileMenu(): void {
    const next = !this.mobileNavOpen();
    this.mobileNavOpen.set(next);
    if (next) {
      this.expanded.set(true);
      this.mobileSettingsStep.set(false);
    }
    this.cdr.markForCheck();
  }

  protected closeMobileNav(): void {
    if (!this.mobileNavOpen() && !this.mobileSettingsStep()) {
      return;
    }
    this.mobileNavOpen.set(false);
    this.mobileSettingsStep.set(false);
    this.cdr.markForCheck();
  }

  protected openSettingsStep(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.expanded.set(true);
    this.mobileSettingsStep.set(true);
    this.cdr.markForCheck();
  }

  protected backToMainNav(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.mobileSettingsStep.set(false);
    this.cdr.markForCheck();
  }

  protected onAsideNavigate(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a')) {
      this.closeMobileNav();
    }
  }


  protected toggleSupportDrawer(): void {
    this.supportDrawerOpen.update(v => {
      const next = !v;
      if (!next) {
        this.supportDrawerExpanded.set(false);
      }
      setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
      return next;
    });
  }

  protected handleDrawerExpandedChange(expanded: boolean): void {
    this.supportDrawerExpanded.set(expanded);
    setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
  }

  protected readonly results$ = this.control.valueChanges.pipe(
    filter(Boolean),
    switchMap((value: string) =>
      timer(2000).pipe(
        map(() => this.filter(value)),
        startWith(null),
      )
    ),
  );

  private filter(query: string): Record<string, readonly Result[]> {
    return Object.entries(DATA).reduce(
      (result, [key, value]) => ({
        ...result,
        [key]: value.filter(({ title, href, subtitle = '' }) =>
          TUI_DEFAULT_MATCHER(title + href + subtitle, query),
        ),
      }),
      {},
    );
  }

  get isDarkMode(): boolean {
    return this.uiThemeService.darkMode();
  }

  get activeThemeIndex(): number {
    return this.isDarkMode ? 1 : 0;
  }

  private readonly navPrefetch = inject(NavPrefetchService);

  /** Prefetch destination route chunk + common APIs on sidebar hover. */
  onNavHover(route: string): void {
    if (!route) return;
    const link = this.getDynamicRouterLink(route);
    this.navPrefetch.onHover(link);
  }

  constructor(
    private uiThemeService: UiThemeService,
    public router: Router,
    private route: ActivatedRoute,
    public apiService: ApiService,
    private navigationService: NavigationService,
    private roleService: RoleService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private parkingModal: ParkingModalService,
    private setupGuideService: SetupGuideService,
    private organizationStateService: OrganizationStateService,
  ) {
    // Automatically notify window and components (charts, tables) to adapt when drawer opens/closes/expands
    effect(() => {
      this.supportDrawerOpen();
      this.supportDrawerExpanded();
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 320);
    });
  }

  ngAfterViewInit(): void {
    // Check if notification banner is visible
    this.checkBannerVisibility();

    // Use MutationObserver to watch for banner visibility changes
    const observer = new MutationObserver(() => {
      this.checkBannerVisibility();
    });

    // Observe changes in the document body for notification banner
    const bannerElement = document.querySelector('.notification-banner');
    if (bannerElement) {
      observer.observe(bannerElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    // Also observe the parent container
    const container = document.querySelector('app-notification-banner');
    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true
      });
    }

    // Listen for bannerVisibilityChanged event for instant layout height update
    window.addEventListener('bannerVisibilityChanged', (e: any) => {
      if (e.detail && typeof e.detail.isVisible === 'boolean') {
        this.hasNotificationBanner.set(e.detail.isVisible);
        this.cdr.detectChanges();
      }
    });

    // MutationObserver handles all changes — no interval fallback needed
  }

  private checkBannerVisibility(): void {
    const bannerElement = document.querySelector('.notification-banner');
    const isVisible = bannerElement !== null &&
      bannerElement instanceof HTMLElement &&
      bannerElement.offsetHeight > 0 &&
      window.getComputedStyle(bannerElement).display !== 'none';
    this.hasNotificationBanner.set(isVisible);
    this.cdr.detectChanges();
  }

  private bannerVisibilityInterval: any;
  private userSyncInterval: any;

  ngOnInit(): void {
    // Periodic silent user & role sync — 5 minutes (reduced from 30s to lower server load)
    this.userSyncInterval = setInterval(() => {
      if (this.apiService.getAuthToken() && typeof document !== 'undefined' && !document.hidden) {
        this.apiService.refreshCurrentUserFromServer(false).subscribe(); // force:false — uses built-in 15s throttle
      }
    }, 5 * 60 * 1000);

    // Initialize current URL signal immediately
    const initialUrl = this.router.url;
    this.currentUrl.set(initialUrl);

    // Sync context from URL immediately on initialization
    this.syncContextFromUrl(initialUrl);

    // Handle page refresh scenario
    this.handlePageRefresh();
    this.navigationService.handlePageRefresh();

    // Store current page URL for vendor mode redirects
    this.storeCurrentPageForVendorRedirect();

    // Check for vendor context FIRST before any role synchronization
    this.checkAndRestoreVendorContext();

    // Only force refresh role if we're NOT in vendor mode AND user is authenticated
    const currentRoleOnInit = sessionStorage.getItem('current_role');
    const currentUserForInit = this.apiService.getCurrentUser();
    if (currentRoleOnInit !== 'vendor' && currentUserForInit && currentUserForInit.id) {
      // Ensure role is synchronized correctly on init (prevents unwanted vendor fallback)
      this.roleService.forceRefreshRole();
    } else {
    }

    // Check if we have a URL parking ID and clear any cached selections for fresh start
    const urlParkingId = this.route.snapshot.params['parkingId'];
    if (urlParkingId) {
      // First, completely reset parking selection state
      this.resetParkingSelectionState();

      // Store ONLY the URL parking ID
      sessionStorage.setItem('urlParkingId', urlParkingId);
      sessionStorage.setItem('parking_id', urlParkingId);

      // Set immediate temporary selection to show URL parking ID
      this.selectedParkingId = urlParkingId;
      this.selectedParkingName = `Parking ${urlParkingId.substring(0, 8)}...`;

      // Create temporary parking object for immediate display
      const tempParkingObject = {
        id: urlParkingId,
        name: `Parking ${urlParkingId.substring(0, 8)}...`
      };

      // Add the temporary parking to the parking list immediately
      this.parkingList = [tempParkingObject, ...this.parkingList];

      // Set form control immediately
      this.updateParkingControl(tempParkingObject);

      // Fetch actual parking details immediately to replace the temporary placeholder with the real name
      this.fetchParkingDetailsAndUpdatePOV(urlParkingId);

      // Force change detection
      this.cdr.detectChanges();
    }

    // Subscribe to setup status changes to refresh navigation items reactively
    this.setupSubscription.add(
      this.setupGuideService.setupStatusChanged$.subscribe(() => {
        // console.log('Setup status changed - Re-loading navigation items');
        this.loadNavigationItems();
        this.cdr.detectChanges();
      })
    );

    // Subscribe to organization name changes for instant updates across the app
    this.setupSubscription.add(
      this.organizationStateService.organization$.subscribe(org => {
        if (org && org.name) {
          this.organizationName = org.name;
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        }
      })
    );

    // Subscribe to current user profile changes for reactive headers/initials & parking sync
    this.setupSubscription.add(
      this.apiService.currentUser$.subscribe(user => {
        if (user) {
          this.currentUser = user;
          // Update organization name if available in the user object
          if (user.company) {
            this.organizationName = user.company;
          }

          // Reactively sync assigned parkings list and check access for non-org users
          const isOrgUser = this.hasOrganizationLevelAccess() || this.isAdminUser() || this.isSuperAdmin();
          if (!isOrgUser && user.parkings && Array.isArray(user.parkings)) {
            const freshParkings = user.parkings.map((p: any) => ({
              id: p.id || p.parking_id || p._id,
              name: this.cleanParkingName(p.name || p.parking_name || '-')
            })).filter((p: any) => !!p.id);

            this.parkingList = freshParkings;

            const activeParkingId = this.selectedParkingId || sessionStorage.getItem('parking_id');
            const stillHasAccess = activeParkingId ? freshParkings.some((p: any) => p.id === activeParkingId) : false;

            if (!stillHasAccess && freshParkings.length > 0) {
              const nextParking = freshParkings[0];
              this.selectedParkingId = nextParking.id;
              this.selectedParkingName = nextParking.name;
              sessionStorage.setItem('selectedParkingId', nextParking.id);
              sessionStorage.setItem('selectedParkingName', nextParking.name);
              sessionStorage.setItem('parking_id', nextParking.id);
              this.updateParkingControl(nextParking);

              const currentUrl = this.router.url || '';
              if (currentUrl.includes('/parking/')) {
                this.router.navigate(['/parking', nextParking.id, 'dashboard']);
              }
            } else if (freshParkings.length === 0 && !this.isPublicAuthRoute()) {
              this.router.navigate(['/no-parking']);
            } else if (stillHasAccess) {
              const matched = freshParkings.find((p: any) => p.id === activeParkingId);
              if (matched && (!this.parkingControl.value || this.parkingControl.value.id !== matched.id)) {
                this.updateParkingControl(matched);
              }
            }
          }

          // Instantly sync role & rebuild navigation items for the new role/permissions
          this.roleService.forceRefreshRole();
          this.navigationService.clearParkingPermissionsCache();
          this.loadNavigationItems();

          this.cdr.markForCheck();
          this.cdr.detectChanges();
        }
      })
    );

    // Initial setup completion check — run immediately so Setup Guide tab
    // reflects completion status right after login (not only after refresh)
    this.throttledSetupCheck(0, true);

    // Get current user information
    this.currentUser = this.apiService.getCurrentUser();

    // Public auth pages must not trigger a user refresh. On a hard refresh this
    // request can fail before Angular has fully restored the route, and the
    // legacy failure handler may otherwise send signup users to /login.
    if (!this.currentUser && !this.isPublicAuthRoute()) {
      this.refreshCurrentUser();
    } else {
      // Validate and fix user data if needed
      this.currentUser = this.apiService.validateAndFixUserData();
    }

    // Console logging for initial user data
    // console.log('=== INITIAL USER DATA LOAD ===');
    if (this.currentUser) {
      // Ensure admin users are not in vendor view mode unless explicitly set
      if (this.isAdminUser() && !this.isVendorViewMode) {
        this.isVendorViewMode = false;
      }
    } else {
      // 
      // '❌ No user data available initially');
    }
    //    console.log('=== END INITIAL USER DATA LOAD ===');

    // Load roles from API to ensure proper role name mapping (only if authenticated)
    const currentUser = this.apiService.getCurrentUser();
    if (currentUser && currentUser.id) {
      this.roleService.getRoles().subscribe(roles => {
        // After roles are loaded, ensure proper role synchronization
        this.ensureRoleSynchronization();
      });
    }

    // Immediately handle URL-based parking pre-selection if we have a URL parking ID
    if (urlParkingId) {
      // Force immediate selection with URL parking ID
      this.forceImmediateUrlParkingSelection(urlParkingId);

      // Try to handle URL-based pre-selection immediately
      this.handleUrlBasedParkingPreSelection();

      // Force load parking list for URL-based pre-selection
      this.forceLoadParkingListForUrl();

      // Force selection after a short delay to ensure component is ready
      setTimeout(() => {
        this.forceUrlParkingSelection(urlParkingId);
      }, 100);

      // Additional attempts with longer delays
      setTimeout(() => {
        this.forceUrlParkingSelection(urlParkingId);
      }, 500);

      setTimeout(() => {
        this.forceUrlParkingSelection(urlParkingId);
      }, 1000);
    }

    // Set organization name from currentUser first (fallback)
    this.organizationName = this.getCurrentOrganizationName();

    // Fetch organization name from API using organization ID
    this.fetchOrganizationNameFromAPI();

    // Force change detection after loading currentUser
    this.cdr.detectChanges();

    // Only force refresh role if we're NOT in vendor mode AND user is authenticated
    const currentRoleAfterUserLoad = sessionStorage.getItem('current_role');
    // Reuse this.currentUser instead of declaring new variable
    if (currentRoleAfterUserLoad !== 'vendor' && this.currentUser && this.currentUser.id) {
      // Force refresh role to ensure proper synchronization after login
      this.roleService.forceRefreshRole();
    } else {
    }

    // Set user role from RoleService
    const currentRoleFromService = this.roleService.getCurrentRole();

    // Special handling for super admin users
    if (this.isSuperAdmin()) {
      // Always ensure super admin role is set correctly
      sessionStorage.setItem('current_role', 'super admin');
      this.roleService.switchRole('super admin');
      this.userRole = 'super admin';
    } else if (currentRoleFromService) {
      this.userRole = currentRoleFromService;
    } else {
      // Try to get role from localStorage first
      const savedRole = sessionStorage.getItem('current_role');
      if (savedRole) {
        this.userRole = savedRole;
        this.roleService.switchRole(savedRole);
      } else {
        // Determine role from user data
        const normalized = this.roleService.normalizeRole({
          roleId: this.currentUser?.role_id,
          roleName: this.currentUser?.role?.name,
          type: this.currentUser?.type,
          email: this.currentUser?.email
        });
        this.userRole = normalized;

        // Always set the role for super admin users, regardless of existing localStorage value
        if (normalized.toLowerCase() === 'super admin') {
          sessionStorage.setItem('current_role', 'super admin');
          this.roleService.switchRole('super admin');
        } else {
          this.roleService.switchRole(normalized);
        }
      }
    }

    // Load roles from API
    this.loadRoles();

    // Check for vendor view mode
    this.checkForVendorViewMode();

    // Listen for parking permissions loaded event to refresh navigation
    const parkingPermissionsHandler = () => {
      // Refresh navigation items when parking permissions are loaded
      this.loadNavigationItems();
      this.cdr.detectChanges();
    };
    window.addEventListener('parkingPermissionsLoaded', parkingPermissionsHandler);

    // Store handler reference for cleanup
    (this as any).parkingPermissionsHandler = parkingPermissionsHandler;

    // Load navigation items immediately
    this.loadNavigationItems();

    // Load parking list based on user's access
    this.loadParkingListFromAPI();

    // Subscribe to role changes for immediate updates
    this.roleSubscription = this.roleService.currentRole$.subscribe(newRole => {
      this.userRole = newRole;

      // Update vendor view mode based on role change
      if (this.isAdminUser() && newRole === 'vendor') {
        this.isVendorViewMode = true;

        // Load parking name from vendor context when switching to vendor role
        this.loadParkingNameFromVendorContext();
      } else if (this.isAdminUser() && newRole === 'admin') {
        this.isVendorViewMode = false;
      }

      this.loadNavigationItems();
      this.cdr.detectChanges();
    });

    // Listen for page visibility changes to handle refresh scenarios
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // Page became visible, ensure navigation is properly loaded
        this.ensureRoleSynchronization();
        this.loadNavigationItems();
        this.cdr.detectChanges();
      }
    });

    // Ensure role synchronization after a short delay (only if not in vendor mode AND user is authenticated)
    setTimeout(() => {
      const currentRoleInTimeout = sessionStorage.getItem('current_role');
      const currentUserForTimeout = this.apiService.getCurrentUser();
      if (currentRoleInTimeout !== 'vendor' && currentUserForTimeout && currentUserForTimeout.id) {
        this.roleService.forceRefreshRole();
      } else {
      }
      this.loadNavigationItems();
      this.cdr.detectChanges();
    }, 100);

    // Listen to local storage changes for setup guide
    window.addEventListener('storage', (event) => {
      if (event.key && event.key.startsWith('is_setup_complete_')) {
        this.loadNavigationItems();
        this.cdr.detectChanges();
      }
    });

    // Also listen for storage events to handle role changes
    window.addEventListener('storage', (event) => {
      if (event.key === 'current_role') {
        const currentPath = window.location.pathname;
        const isParkingUrl = currentPath.includes('/parking/') || currentPath.includes('/vendor/');
        const isOrgUrl = currentPath.includes('/organization/') || currentPath.includes('/admin/') || currentPath.includes('/org/');
        if (!isParkingUrl && !isOrgUrl) {
          this.userRole = this.roleService.getCurrentRole();
          this.loadNavigationItems();
          this.cdr.detectChanges();
        }
      } else if (event.key === 'user_info') {
        // Refresh parking list when user data changes
        this.loadParkingListFromAPI();
        this.cdr.detectChanges();
      }
    });

    // Listen for browser back/forward navigation
    window.addEventListener('popstate', () => {
      // Check if current route should have parking ID and redirect if needed
      setTimeout(() => {
        this.checkAndRedirectToParkingRoute();
      }, 100);
    });

    // Keep aside collapsed when on settings page, report-view, pos, admin-full-report or help-center
    if (this.router.url.startsWith('/settings') || this.isPovSettingsRoute(this.router.url) ||
      this.router.url.includes('report-view') || this.router.url.includes('/pos') || this.router.url.includes('/admin-full-report') || this.router.url.includes('/help-center')) {
      this.expanded.set(false);
    } else {
      this.expanded.set(true);
    }

    // Close dropdown on route change and preserve parking ID across routes
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.isOpen = false;
        this.closeMobileNav();

        // Log navigation events for debugging
        if (event instanceof NavigationEnd) {
          this.enforceActiveUserSession();

          // Sync context from URL to ensure storage matches current POV
          this.syncContextFromUrl(event.url);

          // Force check setup completion for the new context/ID — throttled to every 15s
          this.throttledSetupCheck();

          // Reload navigation items to pick up updated storage values
          this.loadNavigationItems();

          // Trigger change detection for OnPush strategy - use detectChanges for immediate update
          this.cdr.detectChanges();

          // Hide overlay loader after everything is synced and navigation has finished
          setTimeout(() => {
            this.hideOverlayLoader();
          }, 500);
        }

        // Ensure vendor mode persists across navigation
        this.ensureVendorModePersistence();

        // Reload parking list when navigating to a parking page (for admin OR org-level users)
        const urlParkingId = this.route.snapshot.params['parkingId'];
        if (urlParkingId && (this.isAdminUser() || this.hasOrganizationLevelAccess())) {
          // Reload parking list to ensure dropdown is populated
          this.loadParkingListFromAPI();
        }

        // Close sidebar when navigating to settings, report-view, pos, admin-full-report or help-center
        if (this.router.url.startsWith('/settings') || this.isPovSettingsRoute(this.router.url) ||
          this.router.url.includes('report-view') || this.router.url.includes('/pos') || this.router.url.includes('/admin-full-report') || this.router.url.includes('/help-center')) {
          this.expanded.set(false);
        }

        // Preserve parking ID across route changes
        this.preserveParkingIdAcrossRoutes();

        // Check if current route should have parking ID and redirect if needed
        // Use a longer delay for settings routes to ensure proper navigation
        const delay = (this.router.url.startsWith('/settings') || this.isPovSettingsRoute(this.router.url)) ? 200 : 100;
        setTimeout(() => {
          this.checkAndRedirectToParkingRoute();
        }, delay);
      });
    // Don't clear parking selection initially - let the parking loading logic handle it
    // This allows us to show the currently active parking as default

    // Subscribe to parking control value changes
    this.parkingSubscription = this.parkingControl.valueChanges.subscribe(parking => {
      this.onParkingSelect(parking);
    });

    // Subscribe to route parameter changes to handle URL-based parking pre-selection dynamically
    this.route.params.subscribe(params => {
      if (params['parkingId']) {
        // Reload parking list when navigating to a parking page (for admin OR org-level users)
        if (this.isAdminUser() || this.hasOrganizationLevelAccess()) {
          this.loadParkingListFromAPI();
        }
        // Force refresh parking selection for repeated visits
        this.forceRefreshParkingSelection();
      }
    });

    // Check if current route should have parking ID after initialization
    setTimeout(() => {
      this.checkAndRedirectToParkingRoute();
      // Also handle URL-based parking pre-selection after a delay to ensure parking list is loaded
      this.handleUrlBasedParkingPreSelection();
      // Load plan data after parking is selected
      this.loadPlanData();
    }, 500);
  }

  /**
   * Load selected parking from localStorage
   */
  private loadSelectedParkingFromStorage(): void {
    // Check if we have a URL parking ID first (highest priority)
    const urlParkingId = this.route.snapshot.params['parkingId'];
    const storedUrlParkingId = sessionStorage.getItem('urlParkingId');

    if (urlParkingId || storedUrlParkingId) {
      return; // Don't load from localStorage if URL has parking ID
    }

    const storedParkingId = sessionStorage.getItem('selectedParkingId');
    const storedParkingName = sessionStorage.getItem('selectedParkingName');
    if (storedParkingId && storedParkingName) {
      this.selectedParkingId = storedParkingId;
      this.selectedParkingName = storedParkingName;
      // Also ensure the main parking_id is set for API service consistency
      sessionStorage.setItem('parking_id', storedParkingId);
    }
  }

  /**
   * Load parking name from vendor context when switching to vendor role
   */
  private loadParkingNameFromVendorContext(): void {
    // Check for vendor context in localStorage
    const vendorContext = sessionStorage.getItem('vendorContext');
    const selectedParkingForVendor = sessionStorage.getItem('selectedParkingForVendor');

    if (vendorContext) {
      try {
        const vendorData = JSON.parse(vendorContext);
        if (vendorData.parkingName) {
          this.selectedParkingName = vendorData.parkingName;
          this.selectedParkingId = vendorData.parkingId;
        }
      } catch (e) {
      }
    } else if (selectedParkingForVendor) {
      try {
        const parkingData = JSON.parse(selectedParkingForVendor);
        if (parkingData.name) {
          this.selectedParkingName = parkingData.name;
          this.selectedParkingId = parkingData.id;
        }
      } catch (e) {
      }
    }

    // Force change detection to update the header
    this.cdr.detectChanges();
  }

  private loadNavigationItems(): void {
    // Update currentUser first
    this.currentUser = this.apiService.getCurrentUser();

    // Update userRole from RoleService
    this.userRole = this.roleService.getCurrentRole();

    // Ensure user data is loaded before loading navigation items
    if (!this.currentUser) {
      const currentUrl = this.router.url || (typeof window !== 'undefined' ? window.location.pathname : '');
      // During a hard refresh router.url can briefly still be '/'. Include the
      // browser URL so this check recognizes public pages before initial navigation completes.
      const isPublicPage = this.isPublicAuthRoute() || currentUrl.includes('/network-error');
      if (isPublicPage) {
        return;
      }

      // Check if we've exceeded max retries
      if (this.loadNavigationRetryCount >= this.MAX_NAVIGATION_LOAD_RETRIES) {
        // Redirect to login page after max retries
        this.router.navigate(['/login']);
        return;
      }

      this.loadNavigationRetryCount++;

      // Retry after a short delay
      setTimeout(() => {
        this.loadNavigationItems();
      }, 100);
      return;
    }

    // Reset retry counter when user data is successfully loaded
    this.loadNavigationRetryCount = 0;

    // Always load navigation items, even if user data is incomplete
    // The navigation service will handle role-based filtering
    this.navigationItems = this.navigationService.getNavigationItems();
    this.settingsNavigationItems = this.navigationService.getSettingsNavigationItems();

    // console.log(`[Sidenavigation]  Loaded ${this.navigationItems.length} main items and ${this.settingsNavigationItems.length} settings items for role ${this.userRole}`);

    // If no navigation items loaded, try with admin role as fallback
    if (this.navigationItems.length === 0 && !this.isRedirecting) {
      console.warn('[Sidenavigation] ️ No navigation items loaded. Checking session state...');
      const userInfo = localStorage.getItem('user_info');
      const authHeader = getCookie('auth_token');
      const loginPermissions = localStorage.getItem('login_permissions');

      this.navigationItems = this.navigationService.getNavigationItemsForRole('admin');
      this.settingsNavigationItems = this.navigationService.getSettingsNavigationItemsForRole('admin');

      if (this.navigationItems.length > 0) {
        console.log(`[Sidenavigation]  Restored ${this.navigationItems.length} items using admin fallback`);
      } else {
        console.error('[Sidenavigation]  Fallback also failed. Navigation may be empty.');
      }
    }

    // Hide Setup Guide if marked as complete in localStorage for the current context
    const currentUrl = this.getActualCurrentUrl();
    const parkingSpecificPattern = /\/parking\/([a-f0-9-]{36})/i;
    const match = currentUrl.match(parkingSpecificPattern);
    const parkingIdFromUrl = match ? match[1] : this.getCurrentParkingIdFromUrl();
    const parkingId = parkingIdFromUrl || sessionStorage.getItem('parking_id') || this.selectedParkingId;
    const orgId = this.getCurrentOrgIdFromUserInfo();
    const isParkingContext = currentUrl.includes('/parking/') || currentUrl.includes('/vendor/') || (!!parkingId && !currentUrl.includes('/organization/'));

    let storageKey = '';
    if (isParkingContext && parkingId) {
      storageKey = `is_setup_complete_parking_${parkingId}`;
    } else if (orgId) {
      storageKey = `is_setup_complete_org_${orgId}`;
    } else {
      return; // Cannot determine setup status without IDs
    }

    const isSetupComplete = localStorage.getItem(storageKey) === 'true';

    if (isSetupComplete) {
      this.navigationItems = this.navigationItems.filter(item => item.label !== 'Setup Guide' && item.route !== '/setup-guide');
    }

    // Always filter out Reservations from sidenavigation
    this.navigationItems = (this.navigationItems || []).filter(item => {
      const lbl = (item.label || '').toLowerCase();
      const route = (item.route || '').toLowerCase();
      const mod = (item.module || '').toLowerCase();
      return !lbl.includes('reservation') && !route.includes('reservation') && !mod.includes('reservation');
    });

    // Force change detection
    this.cdr.detectChanges();
  }

  private getCurrentOrgIdFromUserInfo(): string {
    const user = this.getCachedStorageItem('user_info', true);
    if (user) {
      return user.org_id || user.organization_id || user.organizationId || '';
    }
    return '';
  }

  // Check for vendor view mode
  private checkForVendorViewMode(): void {
    // This method is now simplified - URL-based parking pre-selection is handled elsewhere
    // to avoid conflicts with the main URL-based pre-selection logic

    // Only handle vendor role switching based on current state, not URL parameters
    const currentRole = this.getCurrentRole();
    const isManual = this.roleService.isManualRoleSwitch();

    // Set vendor view mode based on current role and manual switch status
    if (currentRole === 'vendor' || (this.isAdminUser() && isManual)) {
      this.isVendorViewMode = true;
    } else {
      this.isVendorViewMode = false;
    }
  }

  private loadRoles(): void {
    // Only load roles if user is authenticated
    const currentUser = this.apiService.getCurrentUser();
    if (!currentUser || !currentUser.id) {
      return;
    }

    this.roleService.getRoles().subscribe({
      next: () => {
        this.cdr.detectChanges();
      },
      error: () => {
        // Error loading roles
      }
    });
  }

  private enforceActiveUserSession(): void {
    if (this.statusCheckInProgress || this.forcedLogoutInProgress || this.isPublicAuthRoute()) {
      return;
    }
    const lastCheckAt = Math.max(this.lastStatusCheckAt, this.apiService.lastCurrentUserFetchAt);
    if (Date.now() - lastCheckAt < this.STATUS_CHECK_INTERVAL_MS) {
      return;
    }

    const currentUser = this.apiService.getCurrentUser();
    const userId = currentUser?.id;

    if (!userId) {
      return;
    }

    this.statusCheckInProgress = true;
    this.lastStatusCheckAt = Date.now();

    this.apiService.getUserById(userId).subscribe({
      next: (response: any) => {
        this.statusCheckInProgress = false;
        const userData = response?.data || response;

        if (this.isUserSuspended(userData)) {
          this.forceLogoutForSuspendedUser();
        }
      },
      error: (error) => {
        this.statusCheckInProgress = false;
        console.error('Error checking current user status:', error);
      }
    });
  }

  private isPublicAuthRoute(): boolean {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const url = `${pathname} ${hash} ${this.router.url}`.toLowerCase();
    return url.includes('/signup') || url.includes('/sign-up') || url.includes('/login') || url.includes('/network-error');
  }

  private isUserSuspended(userData: any): boolean {
    const normalizedStatus = String(userData?.status || '').toLowerCase();

    if (normalizedStatus === 'suspend' || normalizedStatus === 'suspended' || normalizedStatus === 'inactive') {
      return true;
    }

    if (typeof userData?.is_active === 'boolean') {
      return userData.is_active === false;
    }

    if (typeof userData?.status === 'boolean') {
      return userData.status === false;
    }

    return false;
  }

  private forceLogoutForSuspendedUser(): void {
    if (this.forcedLogoutInProgress) {
      return;
    }

    this.forcedLogoutInProgress = true;
    this.notificationService.showError('Your account has been suspended. Please contact your administrator.', 4000);

    this.apiService.logout().subscribe({
      next: () => {
        this.router.navigate(['/login']);
      },
      error: () => {
        this.router.navigate(['/login']);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.userSyncInterval) {
      clearInterval(this.userSyncInterval);
    }
    if (this.bannerVisibilityInterval) {
      clearInterval(this.bannerVisibilityInterval);
    }
    // Clean up subscription
    if (this.roleSubscription) {
      this.roleSubscription.unsubscribe();
    }
    if (this.parkingSubscription) {
      this.parkingSubscription.unsubscribe();
    }
    if (this.setupSubscription) {
      this.setupSubscription.unsubscribe();
    }

    // Clean up event listener
    const parkingPermissionsHandler = (this as any).parkingPermissionsHandler;
    if (parkingPermissionsHandler) {
      window.removeEventListener('parkingPermissionsLoaded', parkingPermissionsHandler);
    }
  }

  setTheme(isDark: boolean): void {
    this.uiThemeService.toggleTheme(isDark);
    localStorage.setItem('isDark', String(isDark));
  }

  logout() {
    this.apiService.logout().subscribe({
      next: () => {
        this.router.navigate(['/login']);
      },
      error: () => {
        // Even on error, we should navigate back to login as data has been cleared locally
        this.router.navigate(['/login']);
      }
    });
  }

  private isPlaceholderName(value: unknown): boolean {
    const normalized = String(value ?? '').trim();
    return !normalized || /^user$/i.test(normalized);
  }

  protected getUserDisplayName(): string {
    const user = this.apiService.userSignal() || this.currentUser;
    if (!user) return 'N/A';

    const fromName = user.name?.trim();
    if (!this.isPlaceholderName(fromName)) return fromName;

    const fromParts = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return this.isPlaceholderName(fromParts) ? 'N/A' : fromParts;
  }

  protected getUserInitials(): string {
    const user = this.apiService.userSignal() || this.currentUser;
    if (!user) return 'U';

    const fullName = user?.name || user?.userName || '';
    if (fullName.trim() && !this.isPlaceholderName(fullName)) {
      const parts = fullName.trim().split(/\s+/);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return parts[0][0].toUpperCase();
    }

    const firstName = this.isPlaceholderName(user?.firstName) ? '' : (user?.firstName || '');
    const lastName = user?.lastName || '';

    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();

    if (firstName && lastName) {
      return firstInitial + lastInitial;
    } else if (firstName) {
      return firstInitial;
    } else if (lastName) {
      return lastInitial;
    }

    return 'U';
  }

  protected getUserAvatarSrc(): string {
    // Return profile_image or avatarUrl if available
    return this.currentUser?.profile_image || this.currentUser?.avatarUrl || '';
  }

  protected getUserContactDisplay(): string {
    const user = this.apiService.userSignal() || this.currentUser;
    if (!user) return 'N/A';

    // 1st Priority: Email ID
    const email = user.email || user.emailAddress || user.mail;
    if (email && typeof email === 'string') {
      const trimmedEmail = email.trim();
      const lower = trimmedEmail.toLowerCase();
      if (trimmedEmail && lower !== 'no email' && lower !== 'null' && lower !== 'undefined') {
        return trimmedEmail;
      }
    }

    // 2nd Priority: Phone Number (only if Email is missing/invalid)
    const phone = user.phone || user.mobile || user.phoneNumber || user.phone_number || user.mobile_number || user.mobileNo || user.phoneNo || user.contact;
    if (phone && (typeof phone === 'string' || typeof phone === 'number')) {
      const phoneStr = String(phone).trim();
      if (phoneStr && phoneStr.toLowerCase() !== 'null' && phoneStr.toLowerCase() !== 'undefined') {
        return phoneStr;
      }
    }

    // Fallback if neither email nor phone is present
    return 'N/A';
  }

  /**
   * Get role-specific icon for navigation item
   */
  protected getRoleSpecificIcon(item: NavigationItem): string {
    if (!item) return '';
    return this.navigationService.getRoleSpecificIcon(item);
  }

  /**
   * Get role-specific label for navigation item
   */
  protected getRoleSpecificLabel(item: NavigationItem): string {
    if (!item) return '';
    return this.navigationService.getRoleSpecificLabel(item);
  }

  /**
   * Check if user has access to navigation item
   */
  protected hasAccess(item: NavigationItem): boolean {
    if (!item || !item.roles) return false;

    const currentRole = this.roleService.getCurrentRole();

    const hasAccess = item.roles.includes(currentRole) || item.roles.includes('*');

    return hasAccess;
  }

  /**
   * Check if user has access to settings navigation item
   */
  protected hasSettingsAccess(item: NavigationItem): boolean {
    if (!item) return false;
    return this.navigationService.hasSettingsAccess(item.route);
  }

  /**
   * Get role-specific icon for settings navigation item
   */
  protected getSettingsRoleSpecificIcon(item: NavigationItem): string {
    if (!item) return '';
    return this.navigationService.getRoleSpecificIcon(item);
  }

  /**
   * Get role-specific label for settings navigation item
   */
  protected getSettingsRoleSpecificLabel(item: NavigationItem): string {
    if (!item) return '';
    return this.navigationService.getRoleSpecificLabel(item);
  }

  /**
   * Get current organization name with proper formatting
   * Always shows organization name regardless of role or parking selection
   */
  protected getCurrentOrganizationName(): string {
    const userPov = sessionStorage.getItem('user_pov');
    const storedParkingName = sessionStorage.getItem('selectedParkingName') || this.selectedParkingName;

    // Check if user_pov is parking or vendor view mode and selectedParkingName is available
    if ((userPov === 'parking' || this.isVendorViewMode) && storedParkingName && storedParkingName.trim()) {
      return storedParkingName.trim();
    }

    // If no currentUser, return default
    if (!this.currentUser) {
      return 'Sistem Apps';
    }

    // Check for company name in currentUser (primary field)
    const companyName = this.currentUser.company;

    if (companyName && typeof companyName === 'string' && companyName.trim()) {
      return companyName.trim();
    }

    // Check for organization name in other possible fields
    const orgName = this.currentUser.organization ||
      this.currentUser.organizationName ||
      this.currentUser.orgName ||
      this.currentUser.name;

    if (orgName && typeof orgName === 'string' && orgName.trim()) {
      return orgName.trim();
    }

    // Fallback to default
    return 'Sistem Apps';
  }

  protected getOrganizationInitials(): string {
    const user = this.apiService.userSignal();
    const name = user?.company || this.organizationName || 'Sistem Apps';
    if (!name || name.length === 0) return 'SA';
    return name
      .split(' ')
      .slice(0, 3)
      .map((word: string) => word.charAt(0).toUpperCase())
      .join('');
  }

  /**
   * Fetch organization name from API using organization ID
   */
  private fetchOrganizationNameFromAPI(): void {
    const organizationId = this.apiService.getCurrentOrganizationId();

    if (!organizationId) {
      this.organizationName = 'Sistem Apps';
      return;
    }

    this.apiService.getOrganization(organizationId).subscribe({
      next: (response) => {
        // Check if response has data property (wrapped response)
        if (response && response.data && response.data.name) {
          this.organizationName = response.data.name;
          this.cdr.detectChanges();
        }
        // Check if response is direct organization object (handle any type)
        else if (response && typeof response === 'object' && 'name' in response && response.name) {
          this.organizationName = response.name as string;
          this.cdr.detectChanges();
        }
        // Check if response is an array and first item has name
        else if (response && Array.isArray(response) && response.length > 0 && response[0] && typeof response[0] === 'object' && 'name' in response[0] && response[0].name) {
          this.organizationName = response[0].name as string;
          this.cdr.detectChanges();
        }
        else {
          this.organizationName = 'Sistem Apps';
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        this.organizationName = 'Sistem Apps';
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Simple computed property for testing
   */
  protected get testOrganizationName(): string {
    return this.getCurrentOrganizationName();
  }

  private refreshCurrentUser(): void {
    this.currentUser = this.apiService.getCurrentUser();
    if (!this.currentUser) {
      // console.log('=== REFRESHING USER DATA FROM SERVER ===');
      // Try to refresh user data from server
      this.apiService.refreshUserData().subscribe({
        next: (refreshedUser) => {
          // console.log('User data refreshed from server:', {
          //   id: refreshedUser.id,
          //   email: refreshedUser.email,
          //   hasParkings: !!(refreshedUser.parkings && refreshedUser.parkings.length > 0),
          //   parkingCount: refreshedUser.parkings?.length || 0
          // });
          this.currentUser = refreshedUser;
          this.organizationName = this.getCurrentOrganizationName();
          this.fetchOrganizationNameFromAPI();
          this.loadNavigationItems();
          // Refresh parking list when user data is updated
          this.loadParkingListFromAPI();
          this.cdr.detectChanges();
        },
        error: (error) => {
          const currentUrl = typeof window !== 'undefined' ? window.location.pathname : '';
          if (currentUrl.includes('/signup') || currentUrl.includes('/login') || currentUrl.includes('/network-error')) {
            return;
          }
          // If refresh fails, redirect to login
          window.location.href = '/login';
        }
      });
    } else {
      // Validate and fix user data if needed
      this.currentUser = this.apiService.validateAndFixUserData();

      // Update organization name from currentUser first
      this.organizationName = this.getCurrentOrganizationName();

      // Fetch organization name from API
      this.fetchOrganizationNameFromAPI();

      // Refresh parking list when user data is updated
      this.loadParkingListFromAPI();

      // Force change detection after refreshing user data
      this.cdr.detectChanges();
    }
  }

  /**
   * Handle page refresh scenario
   */
  private handlePageRefresh(): void {
    // Check if this is a page refresh by looking for specific indicators
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const legacyNavigation = window.performance?.navigation;
    const isPageRefresh =
      navigationEntry?.type === 'reload' ||
      legacyNavigation?.type === 1;

    if (isPageRefresh) {
      // Ensure user data is properly restored from localStorage and cookie
      const userInfo = localStorage.getItem('user_info');
      const authToken = getCookie('auth_token');
      if (userInfo && authToken) {
        // User data exists, ensure proper restoration and correct role sync
        this.apiService.setAuthStatus(true);

        // Check if we have vendor context and restore vendor mode FIRST
        this.restoreVendorContextOnRefresh();

        // Only force refresh role if we're NOT in vendor mode AND user is authenticated
        const currentRoleInRefresh = sessionStorage.getItem('current_role');
        const currentUserForRefresh = this.apiService.getCurrentUser();
        if (currentRoleInRefresh !== 'vendor' && currentUserForRefresh && currentUserForRefresh.id) {
          this.roleService.forceRefreshRole();
        } else {
        }
      }
    }
  }

  /**
   * Ensure vendor mode persists across navigation
   */
  private ensureVendorModePersistence(): void {
    const currentRole = this.getCachedStorageItem('current_role', false, true);

    // URL is the source of truth - don't force vendor mode when on an organization URL
    const currentUrl = window.location.pathname;
    const isOrgUrl = currentUrl.includes('/organization/') || currentUrl.includes('/admin/') || currentUrl.includes('/org/');
    const isParkingUrl = currentUrl.includes('/parking/') || currentUrl.includes('/vendor/');

    if (isOrgUrl && !isParkingUrl) {
      return; // On org URL, never force vendor mode
    }

    // If current role is vendor, ensure vendor mode is maintained
    if (currentRole === 'vendor') {
      // Set vendor view mode
      this.isVendorViewMode = true;

      // Ensure role is set to vendor
      this.roleService.switchRole('vendor');
      this.userRole = 'vendor';

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  /**
   * Check and restore vendor context on initialization
   */
  private checkAndRestoreVendorContext(): void {
    // Check if we have vendor context stored
    const vendorContext = sessionStorage.getItem('vendorContext');
    const selectedParkingForVendor = sessionStorage.getItem('selectedParkingForVendor');
    const currentRole = sessionStorage.getItem('current_role');

    // IMPORTANT: Check URL context FIRST
    const currentUrl = window.location.pathname;
    const isOrgUrl = currentUrl.includes('/organization/') || currentUrl.includes('/admin/') || currentUrl.includes('/org/');
    const isParkingUrl = currentUrl.includes('/parking/') || currentUrl.includes('/vendor/');

    // If we are on an organization URL and NOT on a parking URL, don't restore vendor context
    if (isOrgUrl && !isParkingUrl) {
      this.isVendorViewMode = false;
      return;
    }

    // Only attempt to restore vendor context when the saved role is vendor
    if (!currentRole || currentRole.toLowerCase() !== 'vendor') {
      this.isVendorViewMode = false;
      return;
    }

    // If we have vendor context OR current role is vendor, restore vendor mode
    if (vendorContext || selectedParkingForVendor || currentRole === 'vendor') {
      this.restoreVendorContextOnRefresh();
    }
  }

  /**
   * Store current page URL for vendor mode redirects
   */
  private storeCurrentPageForVendorRedirect(): void {
    const currentRole = sessionStorage.getItem('current_role');
    if (currentRole === 'vendor') {
      const currentUrl = window.location.pathname;
      // Check if current URL is a pov-scoped route
      const isPovScopedUrl = this.isPovScopedRoute(currentUrl);

      // Only store settings pages and other vendor-specific pages
      if (currentUrl.includes('/settings/') ||
        isPovScopedUrl ||
        currentUrl.includes('/upgrade-plan') ||
        currentUrl.includes('/session') ||
        currentUrl.includes('/passes') ||
        currentUrl.includes('/customers') ||
        currentUrl.includes('/reports') ||
        currentUrl.includes('/report-view') ||
        currentUrl.includes('/facilities') ||
        currentUrl.includes('/facility-activities') ||
        currentUrl.includes('/setup-guide')) {
        localStorage.setItem('lastVendorPageUrl', currentUrl);
      }
    }
  }

  /**
   * Restore vendor context on page refresh
   */
  private restoreVendorContextOnRefresh(): void {
    // Check if we have vendor context stored
    const vendorContext = sessionStorage.getItem('vendorContext');
    const selectedParkingForVendor = sessionStorage.getItem('selectedParkingForVendor');
    const currentRole = sessionStorage.getItem('current_role');
    const selectedParkingId = sessionStorage.getItem('selectedParkingId');
    const selectedParkingName = sessionStorage.getItem('selectedParkingName');

    // If current role is vendor OR we have vendor context, restore vendor mode
    if (currentRole === 'vendor' || vendorContext || selectedParkingForVendor) {
      try {
        let vendorData = null;

        if (vendorContext) {
          vendorData = JSON.parse(vendorContext);
        } else if (selectedParkingForVendor) {
          const parkingData = JSON.parse(selectedParkingForVendor);
          vendorData = {
            parkingId: parkingData.id,
            parkingName: parkingData.name,
            parkingData: parkingData
          };
        } else if (currentRole === 'vendor' && selectedParkingId) {
          // If we have vendor role but no vendor context, create it from selected parking
          vendorData = {
            parkingId: selectedParkingId,
            parkingName: selectedParkingName || `Parking ${selectedParkingId.substring(0, 8)}...`,
            parkingData: {
              id: selectedParkingId,
              name: selectedParkingName || `Parking ${selectedParkingId.substring(0, 8)}...`
            }
          };
        }

        if (vendorData && vendorData.parkingId) {
          // Set vendor view mode
          this.isVendorViewMode = true;

          // Set selected parking
          this.selectedParkingId = vendorData.parkingId;
          this.selectedParkingName = vendorData.parkingName || vendorData.parkingData?.name;

          // Update localStorage
          sessionStorage.setItem('selectedParkingId', vendorData.parkingId);
          sessionStorage.setItem('selectedParkingName', vendorData.parkingName || vendorData.parkingData?.name);
          sessionStorage.setItem('parking_id', vendorData.parkingId);

          // Switch to vendor role
          this.roleService.switchRole('vendor');

          // Set manual switch flag to ensure role preservation
          this.roleService.markAsManualSwitch();

          this.userRole = 'vendor';

          // Create parking object for form control
          const parkingObject = {
            id: vendorData.parkingId,
            name: vendorData.parkingName || vendorData.parkingData?.name
          };

          // Set form control value
          this.updateParkingControl(parkingObject);

          // Force change detection
          this.cdr.detectChanges();

        } else if (currentRole === 'vendor') {
          // If we have vendor role but no parking data, still set vendor mode  
          // Set vendor view mode
          this.isVendorViewMode = true;

          // Switch to vendor role
          this.roleService.switchRole('vendor');

          // Set manual switch flag to ensure role preservation
          this.roleService.markAsManualSwitch();

          this.userRole = 'vendor';

          // Force change detection
          this.cdr.detectChanges();
        }
      } catch (e) {
      }
    }
  }

  /**
   * Ensure proper role synchronization after page refresh
   */
  private ensureRoleSynchronization(): void {
    // Check if we have vendor context first - if so, don't override it
    const vendorContext = sessionStorage.getItem('vendorContext');
    const selectedParkingForVendor = sessionStorage.getItem('selectedParkingForVendor');
    const currentRole = sessionStorage.getItem('current_role');

    // IMPORTANT: Check URL context
    const currentUrl = window.location.pathname;
    const isOrgUrl = currentUrl.includes('/organization/') || currentUrl.includes('/admin/') || currentUrl.includes('/org/');
    const isParkingUrl = currentUrl.includes('/parking/') || currentUrl.includes('/vendor/');

    // If we are on an organization URL, force admin mode if user is an admin
    if (isOrgUrl && !isParkingUrl) {
      const currentUser = this.apiService.getCurrentUser();
      const normalizedRole = this.roleService.normalizeRole({
        roleId: currentUser?.role_id,
        roleName: currentUser?.role?.name,
        type: currentUser?.type,
        email: currentUser?.email
      });

      if (normalizedRole.toLowerCase() === 'admin' || normalizedRole.toLowerCase() === 'super admin') {
        if (currentRole !== normalizedRole.toLowerCase()) {
          this.roleService.switchRole(normalizedRole.toLowerCase());
          this.userRole = normalizedRole.toLowerCase();
          this.isVendorViewMode = false;
          return;
        }
      }
    }

    // If we have vendor context OR current role is vendor, don't override it
    if (currentRole === 'vendor') {
      // Ensure vendor role stays active if that's the saved role
      this.roleService.switchRole('vendor');
      this.userRole = 'vendor';
      return;
    }

    if (vendorContext || selectedParkingForVendor) {
      // Vendor context exists but saved role isn't vendor – keep user's saved role
      return;
    }

    const currentUser = this.apiService.getCurrentUser();
    const savedRole = sessionStorage.getItem('current_role');

    if (currentUser && savedRole) {
      const normalizedRole = this.roleService.normalizeRole({
        roleId: currentUser.role_id,
        roleName: currentUser.role?.name,
        type: currentUser.type,
        email: currentUser.email
      });
      const activeRole = (normalizedRole && normalizedRole !== 'user') ? normalizedRole : savedRole;
      sessionStorage.setItem('current_role', activeRole.toLowerCase());
      this.roleService.switchRole(activeRole.toLowerCase());
      this.userRole = activeRole.toLowerCase();

      // Force navigation items to reload
      this.loadNavigationItems();
      this.cdr.detectChanges();
    } else if (currentUser && !savedRole) {
      // No role saved, determine from user data
      const normalizedRole = this.roleService.normalizeRole({
        roleId: currentUser.role_id,
        roleName: currentUser.role?.name,
        type: currentUser.type,
        email: currentUser.email
      });

      sessionStorage.setItem('current_role', normalizedRole.toLowerCase());
      this.roleService.switchRole(normalizedRole.toLowerCase());
      this.userRole = normalizedRole.toLowerCase();

      // Force navigation items to reload
      this.loadNavigationItems();
      this.cdr.detectChanges();
    }
  }

  /**
   * Handle user data updates
   */
  private handleUserDataUpdate(): void {
    this.refreshCurrentUser();
    this.loadNavigationItems();
    this.cdr.detectChanges();
  }

  /**
   * Handle organization data updates
   */
  private handleOrganizationDataUpdate(): void {
    this.fetchOrganizationNameFromAPI();
    this.cdr.detectChanges();
  }

  /**
   * Open plan details dialog using the existing PlanDetailsComponent
   */
  protected openPlanDetailsDialog(): void {
    // Use the ViewChild reference to call the component's method
    if (this.planDetailsComponent) {
      this.planDetailsComponent.openPlanDetailsDialog();
    }
  }

  protected getUpgradePlanRoute(): any[] {
    // Get user's POV and context to determine the correct URL pattern
    const userPov = sessionStorage.getItem('user_pov');
    const currentRole = this.roleService.getCurrentRole();

    // Prefer the current URL parking id if present, to avoid stale session storage values.
    const currentUrlParkingId = this.getCurrentUrlParkingId();
    if (currentUrlParkingId) {
      return ['/parking', currentUrlParkingId, 'upgrade-plan'];
    }

    // Check if user is in parking POV (vendor mode)
    if (userPov && userPov.toLowerCase() === 'parking') {
      const parkingId = sessionStorage.getItem('parking_id');
      if (parkingId) {
        return ['/parking', parkingId, 'upgrade-plan'];
      }
    }

    // For admin/super admin with organization context
    const orgId = this.apiService.getCurrentOrganizationId();
    if (orgId && orgId !== 'null' && orgId !== 'undefined') {
      const urlPrefix = userPov ? userPov.toLowerCase() : 'organization';
      return [urlPrefix, orgId, 'upgrade-plan'];
    }

    // Fallback: try to get parking ID from user's parkings
    const userParkings = this.apiService.getUserParkings();
    if (userParkings && userParkings.length > 0) {
      const parkingId = userParkings[0].id;
      return ['/parking', parkingId, 'upgrade-plan'];
    }

    // Final fallback - simple route without parameters
    return ['/upgrade-plan'];
  }

  private getCurrentUrlParkingId(): string | null {
    const url = this.router.url || '';
    const match = url.match(/\/parking\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})(?:\/|$)/i);
    return match ? match[1] : null;
  }

  /**
   * Load parking list based on user's access level
   */
  private loadParkingListFromAPI(): void {
    if (!this.currentUser) {
      this.currentUser = this.apiService.getCurrentUser();
      if (!this.currentUser) {
        this.loadingParking.set(false);
        return;
      }
    }

    this.loadingParking.set(true);

    const orgId = this.apiService.getCurrentOrganizationId();
    const userParkings = this.getAssignedParkingsForCurrentUser();
    const isOrgUser = this.hasOrganizationLevelAccess() || this.isAdminUser() || this.isSuperAdmin();

    // 1. If user has organization-level access and we have an organization ID, load all organization parkings
    if (isOrgUser && orgId) {
      this.loadAllOrganizationParkings(orgId);
      return;
    }

    // 2. Fallback to assigned parkings for parking-scoped users
    if (userParkings.length > 0) {
      this.loadUserAssignedParkings(userParkings);
      return;
    }

    if (orgId) {
      this.loadAllOrganizationParkings(orgId);
    } else {
      this.loadingParking.set(false);
      this.parkingList = [];
    }
  }

  /**
   * Assigned parkings for the logged-in user (login payload / user_info) — not the org-wide list.
   */
  private getAssignedParkingsForCurrentUser(): any[] {
    const fromUser = this.currentUser?.parkings || this.apiService.getUserParkings() || [];
    if (Array.isArray(fromUser) && fromUser.length > 0) {
      return fromUser;
    }
    try {
      const info = JSON.parse(localStorage.getItem('user_info') || '{}');
      if (Array.isArray(info?.parkings) && info.parkings.length > 0) {
        return info.parkings;
      }
    } catch { /* ignore */ }
    return [];
  }

  /**
   * Safely update parking form control without triggering user handlers
   */
  private updateParkingControl(value: any, emitEvent: boolean = false): void {
    if (emitEvent) {
      this.parkingControl.setValue(value);
      return;
    }
    this.parkingControl.setValue(value, { emitEvent: false });
  }

  /**
   * Load all parkings from organization (for organization-level users)
   */
  private loadAllOrganizationParkings(orgId: string): void {
    this.apiService.getParkingListByOrg(orgId).subscribe({
      next: (response: any) => {
        this.loadingParking.set(false);

        // Handle different response structures robustly
        let parkings: any[] = [];

        if (response) {
          if (Array.isArray(response)) {
            parkings = response;
          } else if (Array.isArray(response.data)) {
            parkings = response.data;
          } else if (response.data && Array.isArray(response.data.rows)) {
            parkings = response.data.rows;
          } else if (response.data && Array.isArray(response.data.parkings)) {
            parkings = response.data.parkings;
          } else if (Array.isArray(response.rows)) {
            parkings = response.rows;
          } else if (Array.isArray(response.parkings)) {
            parkings = response.parkings;
          }
        }

        // Transform API data to match our expected format
        this.parkingList = parkings.map((parking: any) => ({
          id: parking.id,
          name: this.cleanParkingName(parking.name || parking.parking_name || '-')
        }));

        // console.log(`Loaded ${this.parkingList.length} parkings for dropdown`);

        // Ensure URL parking ID is in the list if it exists
        this.ensureUrlParkingInList();

        // Continue with parking selection logic
        this.continueWithParkingSelection();

        // Also check if we have a stored URL parking ID that needs to be handled now that the list is loaded
        this.handleStoredUrlParkingId();

        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Error loading parking list:', error);
        this.loadingParking.set(false);
        this.parkingList = [];
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Load user's assigned parkings (for parking-level users)
   */
  private loadUserAssignedParkings(userParkings: any[]): void {
    // Transform user's parking data to match our expected format
    this.parkingList = userParkings.map((parking: any) => ({
      id: parking.id,
      name: this.cleanParkingName(parking.name || parking.parking_name || '-')
    }));

    this.loadingParking.set(false);

    // Continue with parking selection logic
    this.continueWithParkingSelection();

    // Also check if we have a stored URL parking ID that needs to be handled now that the list is loaded
    this.handleStoredUrlParkingId();
  }

  /**
   * Handle stored URL parking ID when parking list becomes available
   */
  private handleStoredUrlParkingId(): void {
    const storedUrlParkingId = sessionStorage.getItem('urlParkingId');

    if (storedUrlParkingId && this.parkingList.length > 0) {
      // Find the parking in the list
      const parkingFromUrl = this.parkingList.find(p => p.id === storedUrlParkingId);

      if (parkingFromUrl) {
        // Clean the parking name
        const cleanName = this.cleanParkingName(parkingFromUrl.name);

        // Update the selection with the actual parking data
        this.selectedParkingId = parkingFromUrl.id;
        this.selectedParkingName = cleanName;

        // Create a clean parking object for the form control
        const cleanParkingObject = {
          id: parkingFromUrl.id,
          name: cleanName
        };

        // Update the form control value
        this.updateParkingControl(cleanParkingObject);

        // Update localStorage with actual parking data
        sessionStorage.setItem('selectedParkingId', parkingFromUrl.id);
        sessionStorage.setItem('selectedParkingName', cleanName);
        sessionStorage.setItem('parking_id', parkingFromUrl.id);

        // IMPORTANT: Clear parking permissions cache when parking changes
        this.navigationService.clearParkingPermissionsCache();

        // Fetch parking details and update user_pov for the selected parking
        this.fetchParkingDetailsAndUpdatePOV(parkingFromUrl.id);

        // Dispatch custom event to notify other components about parking change
        window.dispatchEvent(new CustomEvent('parkingChanged', {
          detail: {
            parkingId: parkingFromUrl.id,
            parkingName: cleanName
          }
        }));

        // Refresh navigation items based on selected parking
        this.loadNavigationItems();

        // Load plan data for the selected parking
        this.loadPlanData();

        // Force change detection
        this.cdr.detectChanges();

      } else {
        console.warn('Ignoring unassigned stored URL parking ID in dropdown:', storedUrlParkingId);
      }

      // Clear the stored URL parking ID since we've handled it
      sessionStorage.removeItem('urlParkingId');
    }
  }

  /**
   * Handle URL-based parking pre-selection
   * This method ensures that when a parking ID is in the URL, it gets pre-selected in the dropdown
   */
  private handleUrlBasedParkingPreSelection(): void {
    const urlParkingId = this.route.snapshot.params['parkingId'];

    if (!urlParkingId) {
      // No parking in URL — if list is ready and nothing selected, default to first
      if (
        !this.selectedParkingId &&
        !this.parkingControl.value &&
        this.parkingList.length > 0 &&
        this.apiService.getUserPov() !== 'organization'
      ) {
        this.applyParkingSelection(this.parkingList[0], true);
      }
      return;
    }

    // Already showing the correct parking — do not clear the dropdown
    if (this.parkingControl.value?.id === urlParkingId || this.selectedParkingId === urlParkingId) {
      sessionStorage.setItem('parking_id', urlParkingId);
      return;
    }

    sessionStorage.setItem('urlParkingId', urlParkingId);
    sessionStorage.setItem('parking_id', urlParkingId);

    if (this.parkingList.length > 0) {
      this.selectParkingFromUrl(urlParkingId);
    } else {
      // Temporary placeholder until list loads
      this.selectedParkingId = urlParkingId;
      this.selectedParkingName = 'Loading Parking...';
      this.updateParkingControl({ id: urlParkingId, name: 'Loading Parking...' });
      sessionStorage.setItem('selectedParkingId', urlParkingId);
      sessionStorage.setItem('selectedParkingName', 'Loading Parking...');
      this.cdr.detectChanges();
    }
  }

  /**
   * Force refresh parking selection for repeated visits
   * This ensures that URL-based pre-selection works consistently on repeated visits
   */
  private forceRefreshParkingSelection(): void {
    const urlParkingId = this.route.snapshot.params['parkingId'];

    if (!urlParkingId) {
      return;
    }

    // Already correctly selected — nothing to refresh
    if (this.parkingControl.value?.id === urlParkingId && this.selectedParkingId === urlParkingId) {
      return;
    }

    this.handleUrlBasedParkingPreSelection();
  }

  /**
   * Select parking from URL when parking list is available.
   * If URL id is missing from the list, fall back to the first parking and sync the URL.
   */
  private selectParkingFromUrl(urlParkingId: string): void {
    let parkingFromUrl = this.parkingList.find(p => p.id === urlParkingId);

    if (!parkingFromUrl) {
      parkingFromUrl = this.parkingList.find(p =>
        p.id?.includes?.(urlParkingId) ||
        urlParkingId?.includes?.(p.id) ||
        p.id?.toLowerCase?.() === urlParkingId?.toLowerCase?.()
      );
    }

    // URL id not in dropdown list → default to first parking and fix the URL
    if (!parkingFromUrl && this.parkingList.length > 0) {
      console.warn('URL parking ID not in list; defaulting to first parking:', urlParkingId);
      parkingFromUrl = this.parkingList[0];
      this.applyParkingSelection(parkingFromUrl, true);
      return;
    }

    if (parkingFromUrl) {
      this.applyParkingSelection(parkingFromUrl, false);
    } else {
      console.warn('URL parking ID is not assigned to current user, skipping dropdown insertion:', urlParkingId);
    }
  }

  /**
   * Apply a parking as the selected dropdown value + session context.
   * Always binds the exact list item so tuiSelect shows the name (not "Select Parking").
   */
  private applyParkingSelection(parking: any, syncUrl: boolean): void {
    if (!parking?.id) return;

    const cleanName = this.cleanParkingName(parking.name);
    // Prefer the exact object from parkingList for identityMatcher
    const listItem = this.parkingList.find(p => p.id === parking.id) || {
      id: parking.id,
      name: cleanName
    };
    if (listItem.name) {
      listItem.name = this.cleanParkingName(listItem.name);
    }

    this.selectedParkingId = listItem.id;
    this.selectedParkingName = listItem.name;

    sessionStorage.setItem('selectedParkingId', listItem.id);
    sessionStorage.setItem('selectedParkingName', listItem.name);
    sessionStorage.setItem('parking_id', listItem.id);
    sessionStorage.removeItem('urlParkingId');

    this.updateParkingControl(listItem);

    this.navigationService.clearParkingPermissionsCache();
    this.fetchParkingDetailsAndUpdatePOV(listItem.id);

    window.dispatchEvent(new CustomEvent('parkingChanged', {
      detail: {
        parkingId: listItem.id,
        parkingName: listItem.name
      }
    }));

    this.loadNavigationItems();
    this.loadPlanData();
    this.cdr.detectChanges();

    if (syncUrl) {
      const currentUrl = this.getActualCurrentUrl();
      const parkingSpecificPattern = /\/parking\/([a-f0-9-]{36})/i;
      if (parkingSpecificPattern.test(currentUrl)) {
        const newUrl = currentUrl.replace(parkingSpecificPattern, `/parking/${listItem.id}`);
        if (newUrl !== currentUrl) {
          this.router.navigateByUrl(newUrl, { replaceUrl: true });
        }
      } else if (!currentUrl.includes(listItem.id)) {
        this.router.navigate(['/parking', listItem.id, 'dashboard'], { replaceUrl: true });
      }
    }
  }

  /**
   * Force immediate URL parking selection
   * This ensures the URL parking ID is selected immediately, even before the parking list is loaded
   */
  private forceImmediateUrlParkingSelection(urlParkingId: string): void {
    // Create immediate parking object
    const immediateParkingObject = {
      id: urlParkingId,
      name: `Parking ${urlParkingId.substring(0, 8)}...`
    };

    // Set immediate selection
    this.selectedParkingId = urlParkingId;
    this.selectedParkingName = immediateParkingObject.name;

    // Set form control immediately
    this.updateParkingControl(immediateParkingObject);

    // Update localStorage
    sessionStorage.setItem('selectedParkingId', urlParkingId);
    sessionStorage.setItem('selectedParkingName', immediateParkingObject.name);
    sessionStorage.setItem('parking_id', urlParkingId);

    // Force change detection
    this.cdr.detectChanges();
  }

  /**
   * Force URL parking selection with multiple attempts
   * This method tries multiple approaches to ensure the URL parking ID gets selected
   */
  private forceUrlParkingSelection(urlParkingId: string): void {
    // Create parking object
    const parkingObject = {
      id: urlParkingId,
      name: `Parking ${urlParkingId.substring(0, 8)}...`
    };

    // Ensure parking is in the list
    const existingParking = this.parkingList.find(p => p.id === urlParkingId);
    if (!existingParking) {
      this.parkingList.unshift(parkingObject); // Add to beginning of list
    }

    // Set selection
    this.selectedParkingId = urlParkingId;
    this.selectedParkingName = parkingObject.name;

    // Set form control
    this.updateParkingControl(parkingObject);

    // Update localStorage
    sessionStorage.setItem('selectedParkingId', urlParkingId);
    sessionStorage.setItem('selectedParkingName', parkingObject.name);
    sessionStorage.setItem('parking_id', urlParkingId);

    // Force change detection multiple times
    this.cdr.detectChanges();
    setTimeout(() => this.cdr.detectChanges(), 50);
    setTimeout(() => this.cdr.detectChanges(), 100);
  }

  /**
   * Force load parking list for URL-based pre-selection
   * This ensures the parking list is loaded even if user doesn't normally have parking access
   */
  private forceLoadParkingListForUrl(): void {
    const urlParkingId = this.route.snapshot.params['parkingId'];
    if (!urlParkingId) return;

    // Respect assigned-only list for non-admin users
    this.loadParkingListFromAPI();
  }

  /**
   * Ensure URL parking ID is in the parking list
   * This adds the URL parking ID to the list if it doesn't exist
   */
  private ensureUrlParkingInList(): void {
    const urlParkingId = this.route.snapshot.params['parkingId'];
    const storedUrlParkingId = sessionStorage.getItem('urlParkingId');
    const effectiveUrlParkingId = urlParkingId || storedUrlParkingId;

    const userParkings = this.getAssignedParkingsForCurrentUser();
    const isOrgUser = this.hasOrganizationLevelAccess() || this.isAdminUser() || this.isSuperAdmin();

    // If user is strictly parking-scoped (non-org user), do not forcefully add an unassigned parking from URL
    if (!isOrgUser && userParkings.length > 0) {
      const isAssigned = userParkings.some((p: any) => (p.id || p.parking_id) === effectiveUrlParkingId);
      if (!isAssigned) {
        return;
      }
    }

    if (effectiveUrlParkingId && this.parkingList.length > 0) {
      // Check if URL parking ID is already in the list
      const urlParkingExists = this.parkingList.find(p => p.id === effectiveUrlParkingId);

      if (!urlParkingExists) {
        // Add URL parking ID to the parking list
        const urlParkingObject = {
          id: effectiveUrlParkingId,
          name: `Parking ${effectiveUrlParkingId.substring(0, 8)}...`
        };

        this.parkingList.push(urlParkingObject);
      }
    }
  }

  /**
   * Clean parking name to remove unwanted characters and fix formatting
   */
  private cleanParkingName(name: any): string {
    if (!name) return '-';
    const strName = typeof name === 'object' ? (name.name || name.parking_name || '-') : String(name);
    return strName
      .trim()
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Completely reset parking selection state
   * This ensures no corrupted or cached data interferes with URL-based selection
   */
  private resetParkingSelectionState(): void {
    const preserveVendorContext = this.shouldPreserveVendorContextState();

    // Clear component state
    this.selectedParkingId = null;
    this.selectedParkingName = null;
    this.updateParkingControl(null);

    // Clear ALL localStorage parking data
    sessionStorage.removeItem('selectedParkingId');
    sessionStorage.removeItem('selectedParkingName');
    sessionStorage.removeItem('parking_id');
    sessionStorage.removeItem('urlParkingId');
    if (!preserveVendorContext) {
      sessionStorage.removeItem('vendorContext');
      sessionStorage.removeItem('selectedParkingForVendor');
      sessionStorage.removeItem('manual_role_switch');
    }

    // Force change detection
    this.cdr.detectChanges();
  }

  /**
   * Determine if vendor context should be preserved (manual vendor mode)
   */
  private shouldPreserveVendorContextState(): boolean {
    const currentRole = this.roleService.getCurrentRole();
    const storedRole = sessionStorage.getItem('current_role');
    const manualSwitchFlag = sessionStorage.getItem('manual_role_switch') === 'true';

    if ((currentRole && currentRole.toLowerCase() === 'vendor') ||
      (storedRole && storedRole.toLowerCase() === 'vendor')) {
      return true;
    }

    return manualSwitchFlag;
  }

  /**
   * Continue with parking selection logic after parking list is loaded
   */
  private continueWithParkingSelection(): void {
    // Load selected parking from session first
    this.loadSelectedParkingFromStorage();

    const urlParkingId = this.route.snapshot.params['parkingId'];
    const storedUrlParkingId = sessionStorage.getItem('urlParkingId');
    const currentParkingId = this.apiService.getCurrentParkingId();
    const isSwitchingToVendor = this.getCurrentRole() === 'vendor' && this.isVendorViewMode;
    const isOrgPov = this.apiService.getUserPov() === 'organization';

    let parkingToSelect: any = null;
    const effectiveUrlParkingId = urlParkingId || storedUrlParkingId;

    if (effectiveUrlParkingId) {
      parkingToSelect = this.parkingList.find(p => p.id === effectiveUrlParkingId);
      if (storedUrlParkingId) {
        sessionStorage.removeItem('urlParkingId');
      }
    }

    if (!parkingToSelect && this.selectedParkingId) {
      parkingToSelect = this.parkingList.find(p => p.id === this.selectedParkingId);
    }

    if (!parkingToSelect && currentParkingId) {
      parkingToSelect = this.parkingList.find(p => p.id === currentParkingId);
    }

    // Default: first parking in the dropdown list
    if (!parkingToSelect && this.parkingList.length > 0 && !isOrgPov) {
      parkingToSelect = this.parkingList[0];
    } else if (!parkingToSelect && isSwitchingToVendor && this.parkingList.length > 0) {
      parkingToSelect = this.parkingList[0];
    }

    if (parkingToSelect) {
      // Sync URL only when selected parking differs from URL (or URL has no parking id)
      const needsUrlSync = !isOrgPov && (
        !urlParkingId || parkingToSelect.id !== urlParkingId
      );

      // Bind exact list item so dropdown shows the name
      this.applyParkingSelection(parkingToSelect, needsUrlSync);

      if (isSwitchingToVendor) {
        sessionStorage.setItem('vendorContext', JSON.stringify({
          parkingId: parkingToSelect.id,
          parkingName: parkingToSelect.name
        }));
      }
    }

    this.handleUrlBasedParkingPreSelection();
    this.checkAndRedirectToParkingRoute();
    this.cdr.detectChanges();
  }

  /**
   * Refresh parking list when user data changes
   */
  public refreshParkingList(): void {
    // Reload parking list based on current user data
    this.loadParkingListFromAPI();
  }

  /**
   * Handle parking selection
   */
  protected onParkingSelect(parking: any): void {
    if (!parking) {
      // Clear parking selection
      this.selectedParkingId = null;
      this.selectedParkingName = null;
      sessionStorage.removeItem('selectedParkingId');
      sessionStorage.removeItem('selectedParkingName');
      sessionStorage.removeItem('parking_id'); // Also clear the main parking_id

      // Clear vendor view mode if it was active
      this.navigationService.clearVendorViewContext();

      // Switch back to admin role
      if (this.isAdminUser()) {
        this.roleService.switchRole('admin');
        // Clear manual switch flag when switching back to admin
        this.roleService.clearManualSwitchFlag();
      }

      // Refresh navigation items
      this.loadNavigationItems();

      // Don't update organization name when parking is cleared - it should remain constant
      // this.organizationName = this.getCurrentOrganizationName();
      this.cdr.detectChanges();

      // Clear parking parameters from URL when parking selection is cleared
      this.clearParkingFromUrl();
      return;
    }

    // Show overlay loader
    this.showOverlayLoader('Parking loading...');

    // Increased delay to ensure loader is painted before logic starts
    setTimeout(() => {
      // Clear URL parking ID since this is a manual selection
      sessionStorage.removeItem('urlParkingId');

      this.selectedParkingId = parking.id;
      this.selectedParkingName = parking.name;

      // Store selected parking in localStorage
      sessionStorage.setItem('selectedParkingId', parking.id);
      sessionStorage.setItem('selectedParkingName', parking.name);
      sessionStorage.setItem('parking_id', parking.id);
      const isOrgUser = this.hasOrganizationLevelAccess() || this.isAdminUser();
      sessionStorage.setItem('user_pov', isOrgUser ? 'organization' : 'parking');

      // 🔥 Force change detection immediately so the upgrade button appears
      this.cdr.detectChanges();

      // IMPORTANT: Clear parking permissions cache when parking changes
      // This will force navigation service to fetch fresh permissions for the new parking
      this.navigationService.clearParkingPermissionsCache();

      // Set vendor view mode context for admin users BEFORE fetching parking details
      // so that user_pov evaluation sees the new 'vendor' role and sets POV to 'parking'
      if (this.isAdminUser()) {
        // Validate parking ID format (should be a UUID)
        if (parking.id && typeof parking.id === 'string' && parking.id.length >= 20) {
          // Store parking data in vendor view context
          const vendorViewData = {
            id: parking.id,
            name: parking.name
          };
          sessionStorage.setItem('selectedParkingForVendor', JSON.stringify(vendorViewData));

          // Switch to vendor role
          this.roleService.switchRole('vendor');

          // Set vendor view mode flag
          this.isVendorViewMode = true;
        }
      }

      // IMPORTANT: Fetch parking details and update user_pov for the selected parking
      // This ensures that each parking has its own POV instead of using login-time POV
      this.fetchParkingDetailsAndUpdatePOV(parking.id);

      // Dispatch custom event to notify other components about parking change
      window.dispatchEvent(new CustomEvent('parkingChanged', {
        detail: {
          parkingId: parking.id,
          parkingName: parking.name
        }
      }));

      // Refresh navigation items based on selected parking
      // This will trigger navigation service to fetch permissions for the new parking
      this.loadNavigationItems();

      // Load plan data for the selected parking
      this.loadPlanData();

      // Force change detection to update parking dropdown visibility
      this.cdr.detectChanges();

      // Navigate to current route with selected parking ID to ensure all data is loaded based on selected parking
      this.navigateWithParkingId(parking.id, parking.name);
    }, 50);
  }

  /**
   * Navigate into a parking. Always opens dashboard; setup-guide stays in the sidebar until complete.
   */
  private navigateWithParkingId(parkingId: string, parkingName: string): void {
    if (this.isRedirecting) {
      return;
    }

    const currentRole = this.roleService.getCurrentRole();
    const isVendorMode = this.isVendorViewMode || currentRole === 'vendor';
    this.isRedirecting = true;

    if (isVendorMode) {
      const vendorContext = {
        parkingId: parkingId,
        parkingName: parkingName,
        timestamp: Date.now()
      };
      sessionStorage.setItem('vendorContext', JSON.stringify(vendorContext));
      sessionStorage.setItem('parking_id', parkingId);
      this.roleService.markAsManualSwitch();
    }

    const currentUrl = (this.router.url && this.router.url !== '/') ? this.router.url : window.location.pathname;
    let targetPage = 'dashboard';
    const segments = currentUrl.split('/').filter(seg => seg.length > 0);
    if (segments.length >= 3 && (segments[0] === 'parking' || segments[0] === 'vendor')) {
      targetPage = segments.slice(2).join('/');
    }

    const finishNav = (page: string) => {
      this.router.navigate(['/parking', parkingId, page]).then(() => {
        setTimeout(() => {
          this.isRedirecting = false;
        }, 1000);
      });
    };

    if (targetPage && targetPage !== 'dashboard' && targetPage !== 'setup-guide') {
      finishNav(targetPage);
      return;
    }

    if (currentRole === 'super admin') {
      finishNav('dashboard');
      return;
    }

    this.setupGuideService.getParkingEntryPage(parkingId).subscribe({
      next: (page) => finishNav(page),
      error: () => finishNav('dashboard')
    });
  }

  /**
   * Clear parking parameters from URL when parking ID is cleared
   */
  private clearParkingFromUrl(): void {
    // Get the base route without parking ID
    const baseRoute = this.getBaseRoute();

    // Navigate to the base route (without parking ID)
    this.router.navigateByUrl(baseRoute);
  }

  /**
   * Clear parking selection
   */
  protected clearParkingSelection(): void {
    this.updateParkingControl(null, true);
    // Also clear the main parking_id from localStorage
    sessionStorage.removeItem('parking_id');

    // Clear parking parameters from URL
    this.clearParkingFromUrl();
  }

  protected readonly stringifyParking = (item: any): string => {
    if (!item) return 'Select Parking';
    if (typeof item === 'object') {
      return item.name ? String(item.name) : (item.id ? String(item.id) : 'Select Parking');
    }
    return String(item);
  };

  /**
   * Identity matcher for parking objects
   */
  protected readonly identityMatcher = (item1: any, item2: any): boolean => {
    const id1 = item1?.id || item1;
    const id2 = item2?.id || item2;
    return id1 && id2 ? id1 === id2 : item1 === item2;
  };

  /**
   * Value content handler for parking display
   */
  protected readonly valueContent = (item: any): string => {
    if (!item) {
      return 'Select Parking';
    }

    // Handle parking object
    if (typeof item === 'object' && item !== null && item.name) {
      return item.name;
    }

    // Handle string values
    return item || 'Select Parking';
  };

  /**
   * Check if current user is super admin
   */
  public isSuperAdmin(): boolean {
    if (!this.currentUser) return false;

    const userRole = this.currentUser.role_id || this.currentUser.role?.name || this.currentUser.type;
    const normalizedRole = userRole?.toLowerCase();

    return normalizedRole === 'super admin' ||
      normalizedRole?.includes('super admin') ||
      this.currentUser.email === 'system_admin@kormoan.com';
  }

  /**
   * Check if user is admin
   */
  public isAdminUser(): boolean {
    if (!this.currentUser) return false;

    // Exempt super admin from being treated as a regular admin
    if (this.isSuperAdmin()) {
      return false;
    }

    const userRole = this.currentUser.role_id || this.currentUser.role?.name || this.currentUser.type;
    const normalizedRole = userRole?.toLowerCase();

    const loginPov = localStorage.getItem('user_pov') || this.currentUser.user_pov;

    return normalizedRole === 'admin' ||
      normalizedRole === 'organization admin' ||
      normalizedRole === 'org admin' ||
      this.currentUser.role_id?.toLowerCase().includes('admin') ||
      this.currentUser.type === 'admin' ||
      loginPov === 'organization';
  }

  /**
   * Check if user is admin or super admin AND currently in admin mode (not vendor mode)
   */
  public isAdminOrSuperAdmin(): boolean {
    if (!this.currentUser) return false;

    // URL-based check: if on a parking URL, never show Admin chip (URL is source of truth)
    const currentUrl = window.location.pathname;
    if (currentUrl.startsWith('/parking/') || currentUrl.startsWith('/vendor/')) {
      return false;
    }

    // First check if user is actually an admin
    const userRole = this.currentUser.role_id || this.currentUser.role?.name || this.currentUser.type;
    const normalizedRole = userRole?.toLowerCase();

    const isActuallyAdmin = normalizedRole === 'admin' ||
      normalizedRole === 'super admin' ||
      this.currentUser.role_id?.toLowerCase().includes('admin') ||
      this.currentUser.type === 'admin' ||
      this.currentUser.email === 'system_admin@kormoan.com';

    if (!isActuallyAdmin) return false;

    // Now check if they're currently in admin mode (not vendor mode)
    const currentRole = this.roleService.getCurrentRole();
    const isCurrentlyAdmin = currentRole && (
      currentRole.toLowerCase() === 'admin' ||
      currentRole.toLowerCase() === 'super admin'
    );

    // Only show role chip if user is admin AND currently in admin mode (not vendor mode)
    return isActuallyAdmin && isCurrentlyAdmin;
  }

  /**
   * Check if user has parking access (either organization level or parking level)
   */
  public hasParkingAccess(): boolean {
    if (!this.currentUser) return false;

    // URL-based check: if on a parking URL, always show parking dropdown (URL is source of truth)
    const currentUrl = window.location.pathname;
    const isAtParkingUrl = currentUrl.startsWith('/parking/') || currentUrl.startsWith('/vendor/');

    // Get current role
    const currentRole = this.getCurrentRole();

    // Admin users should see parking dropdown if they are in vendor view mode OR at a parking URL
    if (this.isAdminUser()) {
      return currentRole === 'vendor' || this.isVendorViewMode || isAtParkingUrl;
    }

    // Check if user has specific parking access
    const userParkings = this.currentUser.parkings || [];
    const hasParkingAccess = userParkings.length > 0;

    // Check if user has organization access
    const hasOrgAccess = this.hasOrganizationLevelAccess();

    // User has access if they have either organization access OR specific parking access
    const result = hasOrgAccess || hasParkingAccess;
    return result;
  }

  /**
   * Check if user has organization-level access (can see ALL parkings in the org).
   * Parking-scoped users with assigned parkings must NOT get org-wide access.
   */
  public hasOrganizationLevelAccess(): boolean {
    if (!this.currentUser) {
      this.currentUser = this.apiService.getCurrentUser();
    }
    if (!this.currentUser) return false;

    const orgId = this.apiService.getCurrentOrganizationId();
    const hasOrgAccess = Boolean(orgId && orgId !== 'null' && orgId !== 'undefined');
    if (!hasOrgAccess) return false;

    if (this.isSuperAdmin()) {
      return true;
    }

    const loginPov = localStorage.getItem('user_pov') || this.currentUser.user_pov;
    if (loginPov === 'organization') {
      return true;
    }

    if (this.isAdminUser()) {
      return true;
    }

    return false;
  }

  /**
   * Check if user has parking-level access
   */
  public hasParkingLevelAccess(): boolean {
    if (!this.currentUser) return false;
    return this.getAssignedParkingsForCurrentUser().length > 0;
  }

  /**
   * Exit vendor view mode and return to admin view
   */
  public exitVendorViewMode(): void {
    // Show overlay loader
    this.showOverlayLoader('Parking loading...');

    // Use the new force exit method to completely clear vendor context
    this.roleService.forceExitVendorMode();

    // Update user_pov to "organization" when exiting vendor/parking view
    sessionStorage.setItem('user_pov', 'organization');

    // Update user_info with new POV (disabled to prevent multi-tab pollution)
    /*
    const userInfoStr = localStorage.getItem('user_info');
    if (userInfoStr) {
      try {
        const userDataToUpdate = JSON.parse(userInfoStr);
        userDataToUpdate.user_pov = 'organization';
        localStorage.setItem('user_info', JSON.stringify(userDataToUpdate));
      } catch (error) {
        console.error('Error updating user_info:', error);
      }
    }
    */

    // Wait a moment for the role change to take effect
    setTimeout(() => {
      // Verify that the role has changed to admin
      const currentRole = this.roleService.getCurrentRole();

      if (currentRole !== 'admin') {
        this.roleService.forceExitVendorMode();
      }

      // Clear vendor view mode context
      this.navigationService.clearVendorViewContext();

      // Clear parking selection
      this.selectedParkingId = null;
      this.selectedParkingName = null;

      // Set vendor view mode flag to false
      this.isVendorViewMode = false;

      // Clear parking control value
      this.updateParkingControl(null);

      // Refresh navigation items
      this.loadNavigationItems();

      // Don't update organization name when exiting vendor view mode - it should remain constant
      // this.organizationName = this.getCurrentOrganizationName();

      // Force change detection to hide parking dropdown
      this.cdr.detectChanges();

      // Get organization ID for navigation
      const organizationId = this.apiService.getCurrentOrganizationId() || sessionStorage.getItem('organization_id');

      // Navigate to organization-scoped dashboard using dynamic POV format
      if (organizationId) {
        const userPov = sessionStorage.getItem('user_pov') || 'organization';
        const urlPrefix = userPov.toLowerCase(); // Use user_pov value directly (e.g., "organization")
        const dashboardUrl = `/${urlPrefix}/${organizationId}/dashboard`;
        // console.log('Exiting vendor mode - navigating to organization dashboard:', dashboardUrl);
        this.router.navigateByUrl(dashboardUrl, { replaceUrl: true }).then(() => {
          // Loader will be hidden by NavigationEnd listener
        }).catch(() => {
          // Fallback to old format if new format fails
          console.log('️ Fallback to old dashboard format');
          this.router.navigate(['/dashboard']).then(() => {
            // Loader will be hidden by NavigationEnd listener
          });
        });
      } else {
        // Fallback if no organization ID
        this.router.navigate(['/dashboard']).then(() => {
          // Loader will be hidden by NavigationEnd listener
        });
      }

      // window.location.reload();

    }, 200);
  }

  /**
   * Show global overlay loader via RoleService
   */
  private showOverlayLoader(text: string = 'Switching View...'): void {
    this.roleService.setRoleSwitching(true, text);
  }

  /**
   * Hide global overlay loader via RoleService
   */
  private hideOverlayLoader(): void {
    this.roleService.setRoleSwitching(false);
  }

  /**
   * Get current selected parking ID
   */
  public getSelectedParkingId(): string | null {
    return this.selectedParkingId;
  }

  /**
   * Get current selected parking name
   */
  public getSelectedParkingName(): string | null {
    return this.selectedParkingName;
  }

  /**
   * Get current parking ID from URL (highest priority) or selected parking
   */
  public getCurrentParkingIdFromUrl(): string | null {
    // Check URL path first (highest priority)
    const urlParkingId = this.route.snapshot.params['parkingId'];
    if (urlParkingId) {
      return urlParkingId;
    }

    // Fallback to selected parking
    return this.selectedParkingId;
  }

  /**
   * Check if we're currently viewing a specific parking
   */
  public isViewingSpecificParking(): boolean {
    return !!this.route.snapshot.params['parkingId'];
  }

  /**
   * Get current parking context for other components to use
   */
  public getCurrentParkingContext(): { id: string; name: string } | null {
    const parkingId = this.getCurrentParkingIdFromUrl();
    if (!parkingId) {
      return null;
    }

    const parking = this.parkingList.find(p => p.id === parkingId);
    if (!parking) {
      return null;
    }

    return {
      id: parking.id,
      name: parking.name
    };
  }

  /**
   * Get the base route without parking ID
   */
  private getBaseRoute(): string {
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/').filter(seg => seg.length > 0);
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    // If it's a POV-scoped route (/:pov/:id/...)
    if (segments.length >= 2 && uuidPattern.test(segments[1])) {
      // Return the POV part + the rest, without any trailing ID
      // Actually, for these routes, the ID IS the POV ID.
      // If there's another UUID at the end, remove it.
      const lastSegment = segments[segments.length - 1];
      if (uuidPattern.test(lastSegment) && segments.length > 2) {
        return '/' + segments.slice(0, segments.length - 1).join('/');
      }
      return currentUrl;
    }

    // Fallback for old format: Check if current URL has a parking ID in the path
    const parkingIdMatch = currentUrl.match(/\/([a-f0-9-]{36})$/);
    if (parkingIdMatch) {
      // Remove the parking ID from the path
      return currentUrl.replace(/\/([a-f0-9-]{36})$/, '');
    }

    return currentUrl;
  }

  /**
   * Get the current route without any parking ID
   */
  private getCurrentRouteWithoutParkingId(): string {
    return this.getBaseRoute();
  }

  /**
   * Preserve parking ID across route changes
   */
  private preserveParkingIdAcrossRoutes(): void {
    // Get the current URL
    const currentUrl = this.router.url;

    // FIRST: Check if we need to clean parking ID from org-level settings
    if (this.shouldCleanParkingIdFromRoute(currentUrl)) {
      this.cleanParkingIdFromOrgLevelRoute();
      return; // Exit early, let the cleaning method handle navigation
    }

    // Get the current parking ID from the selected parking
    const currentParkingId = this.selectedParkingId;

    if (!currentParkingId) {
      return; // No parking selected, nothing to preserve
    }

    // Check if the current URL already has the parking ID
    if (currentUrl.includes(`/${currentParkingId}`)) {
      return; // Parking ID is already in the URL, no need to modify
    }

    // Check if we're on a route that should have parking ID
    // Skip certain routes that shouldn't have parking ID
    if (this.shouldSkipParkingIdForRoute(currentUrl)) {
      return;
    }

    // Prevent infinite redirects
    if (this.isRedirecting) {
      return;
    }

    // Get the current route without parking ID and append the parking ID
    const baseRoute = this.getCurrentRouteWithoutParkingId();

    // Check if baseRoute is already a POV-scoped route (/:pov/:id/...)
    const segments = baseRoute.split('/').filter(seg => seg.length > 0);
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    // If it's already a POV-scoped route (at least 2 segments and second is UUID)
    // AND it's not a /parking/:id route (which we want to preserve)
    if (segments.length >= 2 && uuidPattern.test(segments[1])) {
      // If it already has an ID, don't append another one at the end
      // This prevents /organization/:orgId/dashboard/:parkingId
      return;
    }

    const newUrl = `${baseRoute}/${currentParkingId}`;

    // Set redirect flag to prevent infinite loops
    this.isRedirecting = true;

    // Use normal navigation instead of hard refresh
    this.router.navigateByUrl(newUrl).then(() => {
      // Reset flag after navigation
      setTimeout(() => {
        this.isRedirecting = false;
      }, 1000);
    });
  }

  /**
   * Check if a route should skip parking ID preservation
   */
  private shouldSkipParkingIdForRoute(url: string): boolean {
    // Routes that should not have parking ID appended
    const skipRoutes = [
      '/login',
      '/admin-dashboard',
      '/dashboard',     // Prevent /dashboard/:parkingId if already in org/parking POV
      '/debug',
      '/report-view',
      '/reports',
      '/parkings/create',
      '/parking-add-people',
      '/parking-add-capacity',
      '/review-parking',
      '/subscriptions',
      '/plans',
      '/transactions',
      '/subscribers',
      '/organisations',
      '/sub-price',
      '/plan-price-group',
      '/timezone-settings',
      '/timezone-demo',
      '/global-settings',
      '/billing',
      '/billing-invoice',
      '/network-error',
      '/not-found',
      '/pos',
      '/pos-session',
      '/pos-passes',
      '/pos-facilities',
      '/help-center'
    ];

    // Check if it's a settings route
    if (url.startsWith('/settings')) {
      // Some settings routes should preserve parking ID (parking-specific settings)
      const parkingRelatedSettings = [
        '/settings/parking-profile',
        '/settings/parking',
        '/settings/facilities',
        '/settings/passes',
        '/settings/vehicle-rate',
        '/settings/devices'
      ];

      // Check if user has organization-level access
      const hasOrgLevelAccess = this.hasOrganizationLevelAccess();

      // Organization-level settings that don't need parking ID (for parking-level users)
      const orgLevelSettings = [
        '/settings/profile',
        '/settings/language',
        '/settings/global-settings',
        '/settings/integrations'
      ];

      // For organization-level users, some settings might need parking context
      if (hasOrgLevelAccess) {
        const settingsThatNeedParkingContext = [
          '/settings/team',        // Teams from all parkings
          '/settings/organization-profile', // Org profile might need parking context
          '/settings/facilities'   // Facilities from all parkings
        ];

        if (settingsThatNeedParkingContext.some(route => url.startsWith(route))) {
          return false; // Don't skip, allow parking context for org-level users
        }
      }

      // For parking-level users or other org-level settings
      if (orgLevelSettings.some(route => url.startsWith(route))) {
        return true; // Skip parking ID for org-level settings
      }

      // If it's a parking-related setting, don't skip (preserve parking ID)
      if (parkingRelatedSettings.some(route => url.startsWith(route))) {
        return false;
      }

      // For other settings routes, skip parking ID preservation
      return true;
    }

    const shouldSkip = skipRoutes.some(route => url.startsWith(route));

    return shouldSkip;
  }

  /**
   * Check if a route should be cleaned of parking ID (for org-level settings)
   */
  private shouldCleanParkingIdFromRoute(url: string): boolean {
    // Check if user has organization-level access
    const hasOrgLevelAccess = this.hasOrganizationLevelAccess();

    // If user has organization-level access, they might need parking context for some settings
    if (hasOrgLevelAccess) {
      // For org-level users, some settings might need parking context to show data from all parkings
      const settingsThatNeedParkingContext = [
        '/settings/team',        // Teams from all parkings
        '/settings/organization-profile', // Org profile might need parking context
        '/settings/facilities'   // Facilities from all parkings
      ];

      // Check if current URL contains a parking ID pattern and is a setting that needs parking context
      const hasParkingIdPattern = /\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/.test(url);

      if (hasParkingIdPattern) {
        for (const orgRoute of settingsThatNeedParkingContext) {
          if (url.startsWith(orgRoute)) {
            return false; // Don't clean, keep parking context for org-level users
          }
        }
      }
    }

    // Organization-level settings that should NEVER have parking ID (for parking-level users)
    const orgLevelSettings = [
      '/settings/profile',
      '/settings/language',
      '/settings/global-settings',
      '/settings/integrations'
    ];

    // Check if current URL contains a parking ID pattern and is an org-level setting
    const hasParkingIdPattern = /\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/.test(url);

    if (hasParkingIdPattern) {
      for (const orgRoute of orgLevelSettings) {
        if (url.startsWith(orgRoute)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Clean parking ID from organization-level settings routes
   */
  private cleanParkingIdFromOrgLevelRoute(): void {
    // Prevent infinite redirects
    if (this.isRedirecting) {
      return;
    }

    const currentUrl = this.router.url;

    if (!this.shouldCleanParkingIdFromRoute(currentUrl)) {
      return;
    }

    // Extract the base route without parking ID
    const baseRoute = currentUrl.replace(/\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/, '');

    // Set redirect flag to prevent infinite loops
    this.isRedirecting = true;

    // Use normal navigation instead of hard refresh
    this.router.navigateByUrl(baseRoute).then(() => {
      // Reset flag after navigation
      setTimeout(() => {
        this.isRedirecting = false;
      }, 1000);
    });
  }

  /**
   * Ensure parking ID is set for settings routes
   */
  private ensureParkingIdForSettingsRoute(): void {
    const currentUrl = this.router.url;
    const currentParkingId = this.selectedParkingId;

    if (!currentParkingId) {
      return;
    }

    // Check if this is a parking-related settings route
    const parkingRelatedSettings = [
      '/settings/parking-profile',
      '/settings/parking',
      '/settings/facilities',
      '/settings/passes',
      '/settings/vehicle-rate',
      '/settings/devices'
    ];

    // Check if user has organization-level access
    const hasOrgLevelAccess = this.hasOrganizationLevelAccess();

    // Organization-level settings that don't need parking ID (for parking-level users)
    const orgLevelSettings = [
      '/settings/profile',
      '/settings/language',
      '/settings/global-settings',
      '/settings/integrations'
    ];

    // For organization-level users, some settings might need parking context
    if (hasOrgLevelAccess) {
      const settingsThatNeedParkingContext = [
        '/settings/team',        // Teams from all parkings
        '/settings/organization-profile', // Org profile might need parking context
        '/settings/facilities'   // Facilities from all parkings
      ];

      if (settingsThatNeedParkingContext.some(route => currentUrl.startsWith(route))) {
        // Check if parking ID is already in the URL
        if (!currentUrl.includes(`/${currentParkingId}`)) {
          // Get the base route and append parking ID
          const baseRoute = this.getCurrentRouteWithoutParkingId();
          const newUrl = `${baseRoute}/${currentParkingId}`;

          // Set redirect flag to prevent infinite loops
          this.isRedirecting = true;

          // Use normal navigation instead of hard refresh
          this.router.navigateByUrl(newUrl).then(() => {
            // Reset flag after navigation
            setTimeout(() => {
              this.isRedirecting = false;
            }, 1000);
          });
        }
        return;
      }
    }

    // For parking-level users or other org-level settings
    if (orgLevelSettings.some(route => currentUrl.startsWith(route))) {
      return;
    }

    if (parkingRelatedSettings.some(route => currentUrl.startsWith(route))) {
      // Check if parking ID is already in the URL
      if (!currentUrl.includes(`/${currentParkingId}`)) {
        let newUrl = '';
        const parkingSpecificPattern = /\/parking\/([a-f0-9-]{36})/;
        const genericUuidPattern = /\/([a-f0-9-]{36})/;

        if (parkingSpecificPattern.test(currentUrl)) {
          newUrl = currentUrl.replace(parkingSpecificPattern, `/parking/${currentParkingId}`);
        } else if (genericUuidPattern.test(currentUrl)) {
          newUrl = currentUrl.replace(genericUuidPattern, `/${currentParkingId}`);
        } else {
          // Default for settings
          const cleanPath = currentUrl === '/' ? '/settings/parking-profile' : currentUrl;
          newUrl = `/parking/${currentParkingId}${cleanPath.startsWith('/') ? cleanPath : '/' + cleanPath}`;
        }

        // Set redirect flag to prevent infinite loops
        this.isRedirecting = true;

        // Use normal navigation instead of hard refresh
        this.router.navigateByUrl(newUrl).then(() => {
          // Reset flag after navigation
          setTimeout(() => {
            this.isRedirecting = false;
          }, 1000);
        });
      }
    }
  }

  /**
   * Check if current route should have parking ID and redirect if needed
   */
  private checkAndRedirectToParkingRoute(): void {
    // Prevent infinite redirects
    if (this.isRedirecting) {
      return;
    }

    // CRITICAL: If user is in Organization POV, NEVER redirect to a parking-scoped route
    const userPov = this.apiService.getUserPov();
    if (userPov === 'organization') {
      return;
    }

    // Get the current URL
    const currentUrl = this.getActualCurrentUrl();

    // Skip redirection if the URL is already POV-scoped (starting with pov prefix)
    const povSegments = ['active', 'admin', 'organization', 'parking', 'vendor', 'org'];
    const pathParts = currentUrl.split('/').filter(p => p.length > 0);
    if (pathParts.length >= 1 && povSegments.includes(pathParts[0].toLowerCase())) {
      return;
    }

    // FIRST: Check if we need to clean parking ID from org-level settings
    if (this.shouldCleanParkingIdFromRoute(currentUrl)) {
      this.cleanParkingIdFromOrgLevelRoute();
      return; // Exit early, let the cleaning method handle navigation
    }

    // Special handling for settings routes - ensure parking ID is set
    if (currentUrl.startsWith('/settings')) {
      this.ensureParkingIdForSettingsRoute();
    }

    // Get the current parking ID from the selected parking
    const currentParkingId = this.selectedParkingId;

    if (!currentParkingId) {
      return; // No parking selected, nothing to check
    }

    // Check if the current URL already has the parking ID
    if (currentUrl.includes(`/${currentParkingId}`)) {
      return; // Parking ID is already in the URL, no need to redirect
    }

    // Check if we're on a route that should have parking ID
    if (this.shouldSkipParkingIdForRoute(currentUrl)) {
      return; // Skip this route
    }

    // Get the current route without parking ID and append the parking ID
    let newUrl = '';
    const parkingSpecificPattern = /\/parking\/([a-f0-9-]{36})/;
    const genericUuidPattern = /\/([a-f0-9-]{36})/;

    if (parkingSpecificPattern.test(currentUrl)) {
      newUrl = currentUrl.replace(parkingSpecificPattern, `/parking/${currentParkingId}`);
    } else if (genericUuidPattern.test(currentUrl)) {
      newUrl = currentUrl.replace(genericUuidPattern, `/${currentParkingId}`);
    } else {
      const cleanPath = currentUrl === '/' ? '/dashboard' : currentUrl;
      // Only prepend /parking/:id if NOT already POV-scoped (which genericUuidPattern would have caught)
      newUrl = `/parking/${currentParkingId}${cleanPath.startsWith('/') ? cleanPath : '/' + cleanPath}`;
    }

    // Preserve query parameters
    const currentQueryParams = this.route.snapshot.queryParams;
    const queryString = Object.keys(currentQueryParams).length > 0
      ? '?' + Object.keys(currentQueryParams).map(key => `${key}=${currentQueryParams[key]}`).join('&')
      : '';
    const finalUrl = newUrl + queryString;

    // Set redirect flag to prevent infinite loops
    this.isRedirecting = true;

    // Use normal navigation instead of hard refresh, preserving query parameters
    this.router.navigateByUrl(finalUrl).then(() => {
      // Reset flag after navigation
      setTimeout(() => {
        this.isRedirecting = false;
      }, 1000);
    });
  }

  /**
   * Reset user's role back to their original role
   */
  protected resetToOriginalRole(): void {
    // Reset to original role
    this.roleService.resetToOriginalRole();

    // Clear vendor view mode if it was active
    if (this.isVendorViewMode) {
      this.exitVendorViewMode();
    }

    // Refresh navigation items
    this.loadNavigationItems();

    // Force change detection
    this.cdr.detectChanges();
  }

  /**
   * Check if user can reset their role (i.e., if they're currently in a manually switched role)
   */
  protected canResetRole(): boolean {
    return this.roleService.isManualRoleSwitch();
  }

  /**
   * Get the user's original role for display purposes
   */
  protected getOriginalRole(): string {
    return this.roleService.getOriginalRole();
  }

  /**
   * Check if the current role is a manual switch
   */
  protected isCurrentRoleManualSwitch(): boolean {
    return this.roleService.isManualRoleSwitch();
  }

  /**
   * Get the current role display name with manual switch indicator
   */
  protected getCurrentRoleDisplayName(): string {
    const currentRole = this.roleService.getCurrentRole();
    const isManual = this.roleService.isManualRoleSwitch();

    if (isManual) {
      return `${currentRole} (Manual)`;
    }

    return currentRole;
  }

  /**
   * Get the role switcher tooltip text
   */
  protected getRoleSwitcherTooltip(): string {
    const currentRole = this.roleService.getCurrentRole();
    const originalRole = this.roleService.getOriginalRole();

    if (currentRole.toLowerCase() === originalRole.toLowerCase()) {
      return `Current role: ${currentRole}`;
    } else {
      return `Switched from ${originalRole} to ${currentRole}. Click to switch back.`;
    }
  }

  /**
   * Get the role switcher button text
   */
  protected getRoleSwitcherButtonText(): string {
    const currentRole = this.roleService.getCurrentRole();

    if (this.roleService.isManualRoleSwitch()) {
      return `Reset to ${this.roleService.getOriginalRole()}`;
    }

    return `Switch to ${currentRole === 'admin' ? 'Vendor' : 'Admin'}`;
  }

  /**
   * Check if "Manage this account" button should be shown
   * Only show when admin user has switched to vendor role (manually or via parking URL)
   */
  protected shouldShowManageAccountButton(): boolean {
    // Only admins can see this button
    if (!this.isAdminUser()) return false;

    // URL-based check: if on a parking URL, always show so admin can exit vendor view
    const currentUrl = window.location.pathname;
    if (currentUrl.startsWith('/parking/') || currentUrl.startsWith('/vendor/')) {
      return true;
    }

    // Fallback: show if admin manually switched to vendor role
    const currentRole = this.getCurrentRole();
    return currentRole === 'vendor' && this.roleService.isManualRoleSwitch();
  }

  /**
   * Check if "Upgrade Plan" button should be shown
   * Shows for any vendor role (admin-switched or native) as long as a valid parking ID exists.
   */
  protected shouldShowUpgradePlanButton(): boolean {
    // Only show for admin users who entered parking POV
    if (!this.isAdminUser()) return false;

    // Super admins usually don't have a parking subscription context; hide to avoid confusion
    if (this.isSuperAdmin()) {
      return false;
    }

    // URL-based check: show in parking POV (URL is source of truth)
    const currentUrl = window.location.pathname;
    const isAtParkingUrl = currentUrl.startsWith('/parking/') || currentUrl.startsWith('/vendor/');

    const currentRole = this.getCurrentRole();
    const isVendorRole = currentRole === 'vendor';

    // Only show in vendor/parking view (via URL or role)
    if (!isAtParkingUrl && !isVendorRole) {
      return false;
    }

    // Ensure we have a selected parking
    let parkingId = this.selectedParkingId;
    if (!parkingId) {
      // Fallback: try to get parking ID from the URL or sessionStorage
      const urlParkingId = this.route.snapshot.params['parkingId'];
      const storedParkingId = sessionStorage.getItem('parking_id');
      const selectedParkingId = sessionStorage.getItem('selectedParkingId');
      const fallbackId = urlParkingId || storedParkingId || selectedParkingId;
      if (fallbackId) {
        console.log('Resolved parking ID from fallback:', fallbackId);
        this.selectedParkingId = fallbackId;
        sessionStorage.setItem('selectedParkingId', fallbackId);
        sessionStorage.setItem('parking_id', fallbackId);
        // Force change detection so the template updates immediately
        this.cdr.detectChanges();
      } else {
        console.log('No parking ID found');
        return false;
      }
    }
    return true;
  }

  /**
   * Check if current plan is the highest tier plan
   * Currently, "Pro" is considered the highest plan
   */
  protected isHighestPlan(): boolean {
    if (!this.planName) return false;
    // If the subscription is on trial, expired, or cancelled, the user should always be allowed to upgrade/purchase
    if (this.isTrial || this.isPlanExpired || this.isPlanCancelled) return false;
    const planNameLower = this.planName.toLowerCase().trim();
    // Check if plan name is "Pro" (case-insensitive)
    return planNameLower === '';
  }

  /**
   * Check if Upgrade button should be shown
   * Hide if plan is already the highest plan
   */
  protected shouldShowUpgradeButton(): boolean {
    const show = this.shouldShowUpgradePlanButton() && !this.isHighestPlan();
    return show;
  }

  /**
   * Fetch plan information (plan name and expiry date) for the current parking
   */
  protected loadPlanData(force: boolean = false): void {
    const parkingId = this.apiService.getCurrentParkingId();

    if (!parkingId) {
      this.planName = null;
      this.planExpiryDate = null;
      this.isPlanCancelled = false;
      this.isPlanExpired = false;
      this.isTrial = false;
      this.lastLoadedPlanParkingId = null;
      this.cdr.detectChanges();
      return;
    }

    // Force API refresh if requested or if the parking lot context has changed
    const shouldForceRefresh = force || (this.lastLoadedPlanParkingId !== parkingId);

    // Reset plan info before loading new data to prevent showing stale details from the previous parking lot
    this.planName = null;
    this.planExpiryDate = null;
    this.isPlanCancelled = false;
    this.isPlanExpired = false;
    this.isTrial = false;

    this.lastLoadedPlanParkingId = parkingId;

    this.apiService.getParkingDetail(parkingId, shouldForceRefresh).subscribe({
      next: (response: any) => {
        if (response && (response.data || response)) {
          const parkingData = response.data || response;

          if (parkingData.subscription) {
            const subscription = parkingData.subscription;
            const normalizedStatus = this.normalizeSubscriptionStatus(subscription.status);

            const isTrialStatus = normalizedStatus === 'trial' || normalizedStatus === 'trialing';
            const isTrial = subscription.is_trial === true ||
              subscription.is_trial === 1 ||
              subscription.is_trial === 'true' ||
              subscription.is_trial === '1' ||
              isTrialStatus;

            this.isTrial = isTrial;

            // Calculate expiry date and active/expired status
            const now = Date.now();
            let expiryDate: string | null = subscription.end_date || subscription.next_billing_date || subscription.trial_end_date || null;
            let isExpired = false;
            let hasFutureEndDate = false;

            if (subscription.end_date) {
              const endTime = new Date(subscription.end_date).getTime();
              if (!isNaN(endTime) && endTime > now) {
                hasFutureEndDate = true;
                expiryDate = subscription.end_date;
              }
            }
            if (!hasFutureEndDate && subscription.next_billing_date) {
              const nextTime = new Date(subscription.next_billing_date).getTime();
              if (!isNaN(nextTime) && nextTime > now) {
                hasFutureEndDate = true;
                expiryDate = subscription.next_billing_date;
              }
            }

            if (hasFutureEndDate || normalizedStatus === 'active') {
              isExpired = false;
            } else if (subscription.trial_end_date) {
              const trialTime = new Date(subscription.trial_end_date).getTime();
              isExpired = !isNaN(trialTime) && trialTime <= now;
            } else if (subscription.end_date) {
              const endTime = new Date(subscription.end_date).getTime();
              isExpired = !isNaN(endTime) && endTime <= now;
            }

            // A plan is cancelled/inactive if status matches cancelled list or it is expired
            this.isPlanExpired = isExpired;
            this.isPlanCancelled = ['cancelled', 'canceled', 'cancel', 'inactive'].includes(normalizedStatus) || isExpired;

            // Cache subscription details in both localStorage and sessionStorage for consistency
            const subInfo = {
              status: subscription.status,
              is_trial: isTrial,
              trial_end_date: subscription.trial_end_date,
              next_billing_date: subscription.next_billing_date,
              end_date: subscription.end_date,
              plan_name: subscription.plan?.name || 'Plan'
            };
            localStorage.setItem(`parking_subscription_${parkingId}`, JSON.stringify(subInfo));
            sessionStorage.setItem(`parking_subscription_${parkingId}`, JSON.stringify(subInfo));

            // Notify components (like NotificationBanner) that subscription data has been refreshed
            window.dispatchEvent(new CustomEvent('parkingSubscriptionUpdated', {
              detail: { parkingId, subInfo, isExpired }
            }));

            // Get plan name
            if (subscription.plan?.name && subscription.plan.name.trim() !== '') {
              this.planName = subscription.plan.name;
            } else if (subscription.plan_id) {
              this.planName = 'Loading...';
              // Fetch plan details if plan name is not available
              this.apiService.getPlan(subscription.plan_id).subscribe({
                next: (planResponse: any) => {
                  if (planResponse && planResponse.data) {
                    this.planName = planResponse.data.name || 'Plan';
                  } else if (planResponse && planResponse.name) {
                    this.planName = planResponse.name;
                  } else {
                    this.planName = 'Plan';
                  }
                  this.cdr.detectChanges();
                },
                error: () => {
                  this.planName = 'Plan';
                  this.cdr.detectChanges();
                }
              });
            } else {
              this.planName = 'Plan';
            }

            // Set plan expiry date
            this.planExpiryDate = expiryDate;
          } else {
            localStorage.removeItem(`parking_subscription_${parkingId}`);
            sessionStorage.removeItem(`parking_subscription_${parkingId}`);
            this.planName = null;
            this.planExpiryDate = null;
            this.isPlanCancelled = false;
            this.isPlanExpired = false;
            this.isTrial = false;
          }
        } else {
          this.planName = null;
          this.planExpiryDate = null;
          this.isPlanCancelled = false;
          this.isPlanExpired = false;
          this.isTrial = false;
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.planName = null;
        this.planExpiryDate = null;
        this.isPlanCancelled = false;
        this.isPlanExpired = false;
        this.isTrial = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Format expiry date for display
   */
  protected formatExpiryDate(dateString: string | null): string {
    if (!dateString) return '';

    try {
      const organizationTimezone = this.globalTimezoneService.getCurrentTimezone();
      return this.timezoneService.formatUTCToOrganizationTimezone(
        dateString,
        'd MMM yyyy',
        organizationTimezone
      );
    } catch {
      return dateString;
    }
  }

  /**
   * Sync context (POV, IDs) from current URL to sessionStorage and localStorage
   * This ensures state consistency during direct navigation or "Open in New Tab"
   */
  private syncContextFromUrl(url: string = this.router.url): void {
    const segments = url.split('/').filter(seg => seg.length > 0);
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const povSegments = ['active', 'admin', 'organization', 'parking', 'vendor', 'org'];

    // Check for /pov/uuid/... pattern
    if (segments.length >= 2 && povSegments.includes(segments[0].toLowerCase()) && uuidPattern.test(segments[1])) {
      let urlPov = segments[0].toLowerCase();
      const urlId = segments[1];

      const prevPov = sessionStorage.getItem('user_pov');
      const prevParkingId = sessionStorage.getItem('parking_id');
      const prevOrgId = sessionStorage.getItem('organization_id');

      if (prevPov !== urlPov || (urlPov === 'parking' && prevParkingId !== urlId) || (urlPov !== 'parking' && prevOrgId !== urlId)) {
        this.routerLinkCache.clear();
      }

      const isOrgUser = this.hasOrganizationLevelAccess() || this.isAdminUser();
      const povToSet = isOrgUser ? 'organization' : urlPov;

      sessionStorage.setItem('user_pov', povToSet);
      if (urlPov === 'parking') {
        sessionStorage.setItem('parking_id', urlId);
        sessionStorage.setItem('selectedParkingId', urlId);
        sessionStorage.setItem('urlParkingId', urlId);

        // Update component properties directly from URL ID to ensure instant synchronization
        this.selectedParkingId = urlId;
        this.isVendorViewMode = true;
        this.userRole = 'vendor';

        // Dispatch parkingChanged event to update components like NotificationBanner
        if (prevParkingId !== urlId) {
          window.dispatchEvent(new CustomEvent('parkingChanged', {
            detail: { parkingId: urlId, parkingName: `Parking ${urlId.substring(0, 8)}...` }
          }));
        }

        // If we are in parking POV, ensure vendor context is set for NavigationService/RoleService
        const vendorViewData = { id: urlId, name: `Parking ${urlId.substring(0, 8)}...` };
        sessionStorage.setItem('selectedParkingForVendor', JSON.stringify(vendorViewData));

        // Switch role to vendor if user is admin (to enable vendor view mode)
        if (this.isAdminUser() && this.roleService.getCurrentRole() !== 'vendor') {
          this.roleService.switchRole('vendor');
          this.roleService.markAsManualSwitch();
        }

        // Fetch actual parking details immediately (this will also update selectedParkingName and form control)
        this.fetchParkingDetailsAndUpdatePOV(urlId);

        // Load parking list if empty (safe to call here as it's outside template rendering)
        if (this.parkingList.length === 0) {
          this.loadParkingListFromAPI();
        }

        // 🔥 Load plan data for this parking and force change detection
        this.loadPlanData();
        this.cdr.detectChanges();

      } else {
        sessionStorage.setItem('organization_id', urlId);
        sessionStorage.removeItem('urlParkingId');
        sessionStorage.removeItem('parking_id');
        sessionStorage.removeItem('selectedParkingId');
        sessionStorage.removeItem('selectedParkingName');
        sessionStorage.removeItem('selectedParkingForVendor');
        sessionStorage.removeItem('vendorContext');

        // Reset plan details when not in parking POV to avoid stale data leakage
        this.planName = null;
        this.planExpiryDate = null;
        this.isPlanCancelled = false;
        this.isPlanExpired = false;
        this.isTrial = false;

        // If switching BACK to organization POV from parking, ensure role is switched back to admin/super admin
        if ((this.isAdminUser() || this.isSuperAdmin()) && this.roleService.getCurrentRole() === 'vendor') {
          const targetRole = this.isSuperAdmin() ? 'super admin' : 'admin';
          this.roleService.switchRole(targetRole);
          this.isVendorViewMode = false;
        }

        this.cdr.detectChanges();
      }

      // Update sessionStorage context (tab-isolated) and localStorage (persistent)
      sessionStorage.setItem('user_pov', povToSet);
      if (urlPov === 'parking') {
        sessionStorage.setItem('parking_id', urlId);
      } else {
        sessionStorage.setItem('organization_id', urlId);
      }

      // Sync user_info object in localStorage if it exists
      const userInfoStr = localStorage.getItem('user_info');
      if (userInfoStr) {
        try {
          const userInfo = JSON.parse(userInfoStr);
          let changed = false;

          // RECOVERY: If at a parking URL, we need organization_id and parking name from userInfo to load names correctly in new tabs
          if (urlPov === 'parking') {
            const orgId = userInfo.organization_id || userInfo.org_id;
            if (orgId && !sessionStorage.getItem('organization_id')) {
              sessionStorage.setItem('organization_id', orgId);
            }

            // Try to recover the real parking name for the sidebar dropdown
            const parkings = userInfo.parkings || [];
            const matchingParking = parkings.find((p: any) => p.id === urlId);
            if (matchingParking && matchingParking.name) {
              const realName = matchingParking.name;
              this.selectedParkingName = realName;
              sessionStorage.setItem('selectedParkingName', realName);

              // Update selectedParkingForVendor with real name
              const vendorViewData = { id: urlId, name: realName };
              sessionStorage.setItem('selectedParkingForVendor', JSON.stringify(vendorViewData));
            }
          }

          if (urlPov === 'parking' && userInfo.parking_id !== urlId) {
            userInfo.parking_id = urlId;
            userInfo.parkingId = urlId;
            changed = true;
          }
          if (userInfo.user_pov !== urlPov) {
            userInfo.user_pov = urlPov;
            changed = true;
          }
          if (changed) {
            localStorage.setItem('user_info', JSON.stringify(userInfo));
          }
        } catch (e) {
          // Ignore
        }
      }

      // Update current URL signal
      this.currentUrl.set(url);

      // Force change detection if context changed significantly
      if (prevPov !== urlPov || (urlPov === 'parking' && prevParkingId !== urlId) || (urlPov !== 'parking' && prevOrgId !== urlId)) {
        this.cdr.detectChanges();
      }
    }
  }

  private fetchParkingDetailsAndUpdatePOV(parkingId: string): void {
    const now = Date.now();
    const needsFreshDetail =
      this.lastParkingDetailFetch?.id !== parkingId ||
      now - this.lastParkingDetailFetch.at >= this.PARKING_DETAIL_REFRESH_MS;
    if (needsFreshDetail) {
      this.lastParkingDetailFetch = { id: parkingId, at: now };
    }

    this.apiService.getParkingDetail(parkingId, needsFreshDetail).subscribe({
      next: (response: any) => {
        try {
          const parkingData = response?.data || response;
          const statusValue = String(parkingData.status || '').toLowerCase();
          const parsedOrgId = parkingData?.org_id || parkingData?.organization_id || parkingData?.organization?.id || sessionStorage.getItem('organization_id') || localStorage.getItem('organization_id') || '';

          if (parkingData) {
            const isSuspended = statusValue === 'suspended' && this.apiService.getUserPov() === 'parking';

            if (isSuspended) {
              if (parsedOrgId) {
                this.apiService.getParkingListByOrg(parsedOrgId).subscribe({
                  next: (listRes: any) => {
                    const dataObj = listRes?.data || listRes;
                    const parkings = dataObj?.parkings || (Array.isArray(dataObj) ? dataObj : []);
                    const userParkings = this.apiService.getUserParkings() || [];
                    const userParkingIds = userParkings.map((p: any) => p.id || p.parking_id);
                    const nextParking = parkings.find((p: any) => {
                      const pStatus = String(p.status || p.status?.value || '').toLowerCase();
                      const isPSuspended = pStatus === 'suspended';
                      const isAssigned = userParkingIds.includes(p.id);
                      return p.id !== parkingId && !isPSuspended && isAssigned;
                    });

                    if (nextParking) {
                      const nextName = nextParking.name?.name || nextParking.name || 'next available parking';
                      this.parkingModal.showSuspendedAutoSwitch(nextName, () => {
                        sessionStorage.setItem('parking_id', nextParking.id);
                        sessionStorage.setItem('parkingId', nextParking.id);
                        localStorage.setItem('parking_id', nextParking.id);
                        localStorage.setItem('parkingId', nextParking.id);

                        const userInfoStr = localStorage.getItem('user_info');
                        if (userInfoStr) {
                          try {
                            const userInfo = JSON.parse(userInfoStr);
                            userInfo.parking_id = nextParking.id;
                            userInfo.parkingId = nextParking.id;
                            localStorage.setItem('user_info', JSON.stringify(userInfo));
                          } catch (e) { }
                        }

                        const cleanName = nextParking.name?.name || nextParking.name || 'Parking System';
                        sessionStorage.setItem('selectedParkingId', nextParking.id);
                        sessionStorage.setItem('selectedParkingName', cleanName);
                        localStorage.setItem('selectedParkingId', nextParking.id);
                        localStorage.setItem('selectedParkingName', cleanName);
                        this.router.navigate(['/parking', nextParking.id, 'dashboard']).then(() => {
                          window.location.reload();
                        });
                      });
                    } else {
                      this.showSuspensionDialogAndLogout();
                    }
                  },
                  error: () => this.showSuspensionDialogAndLogout()
                });
              } else {
                this.showSuspensionDialogAndLogout();
              }
              return;
            }
          }

          // Update actual parking name if fetched successfully
          if (parkingData && parkingData.name) {
            const cleanName = this.cleanParkingName(parkingData.name);
            this.selectedParkingName = cleanName;
            sessionStorage.setItem('selectedParkingName', cleanName);

            // Update temporary parking object in parkingList if it exists
            const parkingInList = this.parkingList.find(p => p.id === parkingId);
            if (parkingInList) {
              parkingInList.name = cleanName;
            }

            // Update parking control if it matches the current parking id
            const currentControlVal = this.parkingControl.value;
            if (currentControlVal && currentControlVal.id === parkingId) {
              this.updateParkingControl({ id: parkingId, name: cleanName });
            }

            // Sync vendor context if admin
            if (this.isAdminUser()) {
              const vendorViewData = {
                id: parkingId,
                name: cleanName
              };
              sessionStorage.setItem('selectedParkingForVendor', JSON.stringify(vendorViewData));
            }
          }

          // Extract user_pov and organization_id from parking detail response
          // Check multiple possible locations: user.user_pov, user_pov, or user.userPov
          let userPov: string | null = null;

          // Update organization_id if available to ensure context sync for services
          const orgId = parkingData?.organization_id || parkingData?.organization?.id;
          if (orgId) {
            sessionStorage.setItem('organization_id', orgId);
          }

          // Cache subscription details for NotificationBanner
          if (parkingData.subscription) {
            const sub = parkingData.subscription;
            const subInfo = {
              status: sub.status,
              is_trial: sub.is_trial,
              trial_end_date: sub.trial_end_date,
              next_billing_date: sub.next_billing_date,
              end_date: sub.end_date,
              plan_name: sub.plan?.name || 'Plan'
            };
            sessionStorage.setItem(`parking_subscription_${parkingId}`, JSON.stringify(subInfo));
          } else {
            sessionStorage.removeItem(`parking_subscription_${parkingId}`);
          }

          if (parkingData?.user?.user_pov) {
            userPov = parkingData.user.user_pov;
          } else if (parkingData?.user_pov) {
            userPov = parkingData.user_pov;
          } else if (parkingData?.user?.userPov) {
            userPov = parkingData.user.userPov;
          }

          // Update user_pov in user_info and localStorage if found
          if (userPov) {
            userPov = userPov.toLowerCase();
            if (userPov === 'none' || userPov === 'null') {
              const role = sessionStorage.getItem('current_role');
              userPov = (role === 'super admin' || role === 'admin') ? 'organization' : 'parking';
            }
            // Store user_pov separately in sessionStorage for easy access
            sessionStorage.setItem('user_pov', userPov);

            // Do not write tab-specific navigation context to shared localStorage to prevent multi-tab pollution
            /*
            const userInfoStr = localStorage.getItem('user_info');
            if (userInfoStr) {
              const userDataToUpdate = JSON.parse(userInfoStr);
              userDataToUpdate.user_pov = userPov;

              // Save updated user data to localStorage
              localStorage.setItem('user_info', JSON.stringify(userDataToUpdate));
            }
            */

            // If user_pov is "parking", navigate to parking-scoped URL only if not already on a route for this parking
            if (userPov.toLowerCase() === 'parking') {
              const currentParkingId = sessionStorage.getItem('parking_id') || parkingId;
              if (currentParkingId) {
                const currentUrl = (this.router.url && this.router.url !== '/') ? this.router.url : window.location.pathname;
                const urlContainsParking = currentUrl.includes(currentParkingId) || window.location.pathname.includes(currentParkingId);

                if (!urlContainsParking) {
                  // Extract current page from URL if on a pov-scoped route
                  let currentPage = 'dashboard';
                  const segments = currentUrl.split('/').filter(seg => seg.length > 0);
                  if (segments.length >= 3) {
                    // Check if it's a pov-scoped route using standard UUID pattern
                    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                    if (uuidPattern.test(segments[1]) && segments.length >= 3) {
                      currentPage = segments[2];
                      // Handle nested routes like settings/org-profile
                      if (segments.length >= 4 && segments[2] === 'settings') {
                        currentPage = `settings/${segments[3]}`;
                      }
                    }
                  }

                  const urlPrefix = userPov.toLowerCase();
                  const comingFromOrgRoute = !currentUrl.includes('/parking/');
                  const shouldCheckSetup = comingFromOrgRoute || currentPage === 'dashboard';

                  if (shouldCheckSetup) {
                    this.setupGuideService.getParkingEntryPage(currentParkingId).subscribe({
                      next: (page) => {
                        this.router.navigate([urlPrefix, currentParkingId, page]).catch(err => {
                          console.error('Error navigating to pov route:', err);
                        });
                      },
                      error: () => {
                        this.router.navigate([urlPrefix, currentParkingId, 'dashboard']);
                      }
                    });
                  } else {
                    this.router.navigate([urlPrefix, currentParkingId, currentPage]).catch(err => {
                      console.error('Error navigating to pov route:', err);
                    });
                  }
                }
              }
            }

            // Refresh navigation items to reflect new POV
            this.loadNavigationItems();
            this.cdr.detectChanges();
          } else {
            console.log('️ Navigation Debug - No user_pov found in parking details response');
            this.loadNavigationItems();
            this.cdr.detectChanges();
          }
        } catch (error) {
          console.error('Navigation Debug - Error updating user_pov from parking detail:', error);
          this.loadNavigationItems();
          this.cdr.detectChanges();
        }
      },
      error: (error: any) => {
        console.error('Navigation Debug - Error fetching parking details for POV:', error);
        this.loadNavigationItems();
        this.cdr.detectChanges();
      }
    });
  }

  private normalizeSubscriptionStatus(status: any): string {
    if (typeof status === 'string') {
      return status.toLowerCase();
    }
    if (status === false || status === 0) {
      return 'inactive';
    }
    if (status === true || status === 1) {
      return 'active';
    }
    return '';
  }

  private showSuspensionDialogAndLogout(): void {
    this.parkingModal.showSuspendedAdmin();
  }

  protected confirmSuspendedAutoSwitch(): void { }
  protected closeSuspendedAdminModal(): void { }
}
